import { Ionicons } from '@expo/vector-icons';
import { useIsFocused } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { deviceKeys } from '../hooks/queries';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  FlatList,
  I18nManager,
  Modal,
  Platform,
  RefreshControl,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useHeaderHeight } from '@react-navigation/elements';
import { getDeviceTypeLabel } from '../constants/deviceTypes';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { DevicesStackParamList } from '../navigation/DevicesStackNavigator';
import { deviceApi, zoneApi } from '../services/api';
import { getErrorMessage, parseError } from '../utils/errorHandler';
import logger from '../utils/logger';
// P1-03: Shared UI components for state handling
import EmptyState from '../components/EmptyState';
import ErrorView from '../components/ErrorView';
import LoadingScreen from '../components/LoadingScreen';
import {
  CameraEvent,
  formatStreamStatus,
  getCameraStatus,
  StreamStatus,
} from '../utils/cameraCalculations';
import { DoorState, formatDoorState } from '../utils/doorCalculations';
import {
  EnergyEvent,
  formatCost,
  formatPower,
  getEnergyStatus,
  TariffRate,
} from '../utils/energyCalculations';
import { formatTimeSince } from '../utils/motionCalculations';
import {
  calculatePercentage,
  calculateRemainingVolume,
  formatPercentage,
  formatVolume,
} from '../utils/tankCalculations';

// 🔌 Power Outlet Calculations (v2.4.0)
import {
  formatCostOutlet,
  formatDuration,
  formatOutletState,
  formatPowerOutlet,
  getOutletStatus,
  type OutletState,
} from '../utils/powerCalculations';

// ⚙️ Actuator Device Calculations (v2.4.0)
import {
  formatActuatorState,
  formatOperationCount,
  formatPosition,
  getActuatorStatus,
  type ActuatorState,
} from '../utils/actuatorCalculations';

// 🌬️ Air Quality Sensor Calculations (v2.4.0)
import {
  formatAQI,
  formatCO2,
  formatPM25,
  formatVOC,
  getAirQualityStatus,
} from '../utils/airQualityCalculations';

// Light Sensor imports
import {
  formatBrightnessLevel,
  formatLux,
  getBrightnessColor,
  getLightStatus,
} from '../utils/lightCalculations';

// Pressure Sensor imports
import {
  formatPressure,
  formatPressureLevel,
  getPressureLevelColor,
  getPressureStatus,
  getWeatherForecastColor,
} from '../utils/pressureCalculations';

// Gas Sensor imports
import {
  formatConcentration,
  formatToxicityLevel,
  getGasStatus,
  getToxicityLevelColor,
} from '../utils/gasCalculations';

// Smoke Detector imports
import {
  formatDensityLevel,
  formatDensity as formatSmokeDensity,
  getDensityLevelColor,
  getSmokeStatus,
  type SmokeReading,
} from '../utils/smokeCalculations';

// 💧 Water Leak Sensor Imports (v2.4.0)
import {
  formatWaterLevel,
  formatWaterStatus,
  getWaterLeakStatus,
  getWaterStatusColor,
  type WaterLeakReading,
} from '../utils/waterLeakCalculations';

import { canManageDevices } from '../utils/permissions';

type DevicesScreenNavigationProp = StackNavigationProp<DevicesStackParamList, 'DevicesList'>;

interface Props {
  navigation: DevicesScreenNavigationProp;
  route: any;
}

interface Device {
  id: string;
  name: string;
  type: string;
  deviceType?: string;
  status: 'online' | 'offline' | 'warning';
  lastSeenAt?: string;
  zoneId?: string;
  zone?: {
    id: string;
    name: string;
  };
  battery?: number;
  hasBattery?: boolean;
  signal?: number;
  tankCapacityLiters?: number; // ✅ إضافة حقل سعة الخزان
  tankHeightCm?: number; // ✅ إضافة حقل ارتفاع الخزان
  latestReading?: {
    temperatureC?: number;
    humidityRh?: number;
    levelCm?: number;
    batteryV?: number;
    batteryPct?: number;
    rssiDbm?: number;
    // Rotating equipment and solar report these; without them a motor or an
    // inverter renders a card with no readings at all.
    vibrationMmS?: number;
    powerKw?: number;
    ts: string;
  };
  latestTelemetry?: {
    last_ts?: string;
    [key: string]: any;
  };
}

interface Zone {
  id: string;
  name: string;
}

type SortOption =
  | 'name-asc'
  | 'name-desc'
  | 'type'
  | 'status'
  | 'battery-low'
  | 'battery-high'
  | 'recent';

export default function DevicesScreen({ navigation, route }: Props) {
  const { t, i18n } = useTranslation();
  const { theme } = useTheme();
  const { user } = useAuth();
  const insets = useSafeAreaInsets();
  // Header is transparent (see DevicesStackNavigator) — push content below it
  const headerHeight = useHeaderHeight();
  const isArabic = i18n.language === 'ar';
  const isRTL = I18nManager.isRTL || isArabic;
  const userCanManageDevices = canManageDevices(user?.roles || []);
  const queryClient = useQueryClient();

  // React Query: fetch devices
  const {
    data: rawDevices = [],
    isLoading,
    error: queryError,
    refetch,
    isRefetching,
  } = useQuery({
    queryKey: deviceKeys.list(user?.orgId || ''),
    queryFn: async () => {
      const devicesData = await deviceApi.getDevices(user?.orgId || '');
      if (!Array.isArray(devicesData)) {
        throw new Error(
          i18n.language === 'ar' ? 'استجابة غير صالحة من الخادم' : 'Invalid response from server',
        );
      }
      return devicesData;
    },
    enabled: !!user,
    staleTime: 30_000,
    retry: 2,
  });

  // React Query: fetch zones
  const { data: zones = [] } = useQuery({
    queryKey: ['zones', 'list', user?.orgId || ''],
    queryFn: async () => {
      if (!user?.orgId) return [];
      const zonesData = await zoneApi.getZones(user.orgId);
      return Array.isArray(zonesData) ? zonesData : [];
    },
    enabled: !!user?.orgId,
    staleTime: 60_000,
  });

  const error = queryError ? (queryError as Error).message : null;

  // Local enrichment: telemetry readings merged onto devices
  // ✅ FIX: /devices already returns latestTelemetry — no extra API calls needed
  const devices = useMemo(
    () =>
      rawDevices.map((device: any) => {
        const lt = device.latestTelemetry;
        if (!lt) return device;
        return {
          ...device,
          latestReading: {
            temperatureC: lt.temperature_c ?? undefined,
            humidityRh: lt.humidity_rh ?? undefined,
            levelCm: lt.level_cm ?? undefined,
            batteryV: lt.battery_v ?? undefined,
            batteryPct: lt.battery_pct ?? undefined,
            rssiDbm: lt.rssi_dbm ?? undefined,
            vibrationMmS: lt.vibration_mm_s ?? undefined,
            powerKw: lt.power_kw ?? undefined,
            ts: lt.last_ts,
          },
        };
      }),
    [rawDevices],
  );
  const [filterStatus, setFilterStatus] = useState<'all' | 'online' | 'offline' | 'warning'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);

  // Advanced filters
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [selectedDeviceTypes, setSelectedDeviceTypes] = useState<string[]>([]);
  const [selectedZones, setSelectedZones] = useState<string[]>([]);
  const [batteryFilter, setBatteryFilter] = useState<'all' | 'low' | 'medium' | 'high'>('all');
  const [sortBy, setSortBy] = useState<SortOption>('name-asc');

  // Bulk operations
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<string[]>([]);
  const [showBulkZoneModal, setShowBulkZoneModal] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    visible: boolean;
    message: string;
    type: 'success' | 'error';
  }>({
    visible: false,
    message: '',
    type: 'success',
  });

  // P0: focus guard + 600 ms throttle — prevent dual-Modal crash on iOS 17 (RNSScreen.mm:1968)
  const isFocused = useIsFocused();
  const isPresentingRef = useRef(false);

  const openFilters = () => {
    if (!isFocused || isPresentingRef.current) return;
    isPresentingRef.current = true;
    setShowBulkZoneModal(false);
    setShowFiltersModal(true);
    setTimeout(() => {
      isPresentingRef.current = false;
    }, 600);
  };

  const openBulkZone = () => {
    if (!isFocused || isPresentingRef.current) return;
    isPresentingRef.current = true;
    setShowFiltersModal(false);
    setShowBulkZoneModal(true);
    setTimeout(() => {
      isPresentingRef.current = false;
    }, 600);
  };

  // تطبيق الفلتر من route params (منفصل للتأكد من التطبيق الفوري)
  useEffect(() => {
    if (route?.params?.filter) {
      logger.info('Applying filter from route params:', route.params.filter);
      setFilterStatus(route.params.filter);
    }
  }, [route?.params?.filter]);

  // إعادة التحميل عند تغيير route params (بعد تعديل جهاز)
  useEffect(() => {
    if (route?.params?.refresh && user?.orgId) {
      logger.info('Refreshing devices due to route param change');
      queryClient.invalidateQueries({ queryKey: deviceKeys.all });
    }
  }, [route?.params?.refresh]);

  const onRefresh = () => {
    refetch();
  };

  const getLastSeenTimestamp = (device: Device) => {
    return device.lastSeenAt || device.latestTelemetry?.last_ts || device.latestReading?.ts;
  };

  const isDeviceOnline = (device: Device) => {
    const lastSeenTs = getLastSeenTimestamp(device);
    if (!lastSeenTs) {
      return device.status === 'online';
    }

    return new Date().getTime() - new Date(lastSeenTs).getTime() < 15 * 60 * 1000;
  };

  const getStatusStyle = (device: any) => {
    const isArabic = i18n.language === 'ar';
    const isOnline = isDeviceOnline(device);

    if (isOnline) {
      return {
        bg: theme.colors.success + '20',
        text: theme.colors.success,
        icon: 'checkmark-circle' as const,
        label: isArabic ? 'متصل' : 'Online',
      };
    } else {
      return {
        bg: theme.colors.error + '20',
        text: theme.colors.error,
        icon: 'close-circle' as const,
        label: isArabic ? 'غير متصل' : 'Offline',
      };
    }
  };

  const getDeviceIcon = (type: string): keyof typeof Ionicons.glyphMap => {
    const typeMap: Record<string, keyof typeof Ionicons.glyphMap> = {
      temperature: 'thermometer',
      humidity: 'water',
      level: 'speedometer',
      pressure: 'speedometer-outline',
      motion: 'walk',
      door: 'lock-closed',
      default: 'hardware-chip',
    };
    return typeMap[type?.toLowerCase()] || typeMap.default;
  };

  // Get unique device types
  const deviceTypes = useMemo(() => {
    const types = new Set(devices.map((d) => d.type).filter(Boolean));
    return Array.from(types).sort();
  }, [devices]);

  // Count active filters
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (selectedDeviceTypes.length > 0) count++;
    if (selectedZones.length > 0) count++;
    if (batteryFilter !== 'all') count++;
    if (sortBy !== 'name-asc') count++;
    return count;
  }, [selectedDeviceTypes, selectedZones, batteryFilter, sortBy]);

  // Clear all filters
  const clearAllFilters = () => {
    setSelectedDeviceTypes([]);
    setSelectedZones([]);
    setBatteryFilter('all');
    setSortBy('name-asc');
    setSearchQuery('');
    setFilterStatus('all');
  };

  const showSnackbar = useCallback((message: string, type: 'success' | 'error' = 'success') => {
    setSnackbar({ visible: true, message, type });
    setTimeout(() => {
      setSnackbar((prev) => ({ ...prev, visible: false }));
    }, 2600);
  }, []);

  // Bulk operations functions
  const toggleSelectionMode = useCallback(() => {
    setSelectionMode((prev) => !prev);
    setSelectedDeviceIds([]);
  }, []);

  // ✅ P1-08: Memoize callback to prevent re-creation on every render
  const toggleDeviceSelection = useCallback(
    (deviceId: string) => {
      if (selectedDeviceIds.includes(deviceId)) {
        setSelectedDeviceIds((prev) => prev.filter((id) => id !== deviceId));
      } else {
        setSelectedDeviceIds((prev) => [...prev, deviceId]);
      }
    },
    [selectedDeviceIds],
  );

  const deselectAllDevices = () => {
    setSelectedDeviceIds([]);
  };

  const handleBulkDelete = async () => {
    if (selectedDeviceIds.length === 0) return;

    const confirmMessage =
      i18n.language === 'ar'
        ? `هل تريد حذف ${selectedDeviceIds.length} جهاز؟`
        : `Delete ${selectedDeviceIds.length} device(s)?`;

    // Platform-specific confirmation
    let confirmed = false;
    if (Platform.OS === 'web') {
      confirmed = confirm(confirmMessage);
    } else {
      // Use Alert.alert for mobile with buttons
      Alert.alert(i18n.language === 'ar' ? 'تأكيد الحذف' : 'Confirm Delete', confirmMessage, [
        {
          text: i18n.language === 'ar' ? 'إلغاء' : 'Cancel',
          style: 'cancel',
        },
        {
          text: i18n.language === 'ar' ? 'حذف' : 'Delete',
          style: 'destructive',
          onPress: async () => {
            await performBulkDelete();
          },
        },
      ]);
      return; // Exit early for mobile (callback handles it)
    }

    // For web, continue directly if confirmed
    if (confirmed) {
      await performBulkDelete();
    }
  };

  const performBulkDelete = async () => {
    logger.log('🗑️ Starting bulk delete for devices:', selectedDeviceIds);
    const results = await Promise.allSettled(
      selectedDeviceIds.map((id) => deviceApi.deleteDevice(id)),
    );

    const succeeded = results.filter((r) => r.status === 'fulfilled').length;
    const failed = results.filter((r) => r.status === 'rejected').length;
    const hasPermissionError = results
      .filter((r): r is PromiseRejectedResult => r.status === 'rejected')
      .some(
        (r) =>
          r.reason?.response?.status === 403 ||
          String(r.reason?.message || '').includes('permissions'),
      );

    setSelectedDeviceIds([]);
    setSelectionMode(false);
    queryClient.invalidateQueries({ queryKey: deviceKeys.all });

    if (failed === 0) {
      const successMessage =
        i18n.language === 'ar' ? `تم حذف ${succeeded} جهاز` : `Deleted ${succeeded} device(s)`;
      showSnackbar(successMessage, 'success');
    } else if (succeeded === 0) {
      showSnackbar(
        hasPermissionError
          ? i18n.language === 'ar'
            ? 'لا تملك صلاحية حذف الأجهزة. يلزم دور مدير النظام.'
            : 'No permission to delete devices. Admin role required.'
          : i18n.language === 'ar'
          ? 'فشل حذف الأجهزة'
          : 'Failed to delete devices',
        'error',
      );
    } else {
      showSnackbar(
        i18n.language === 'ar'
          ? `تم حذف ${succeeded} جهاز، فشل حذف ${failed} جهاز`
          : `Deleted ${succeeded}, failed ${failed} device(s)`,
        'error',
      );
    }
  };

  const handleBulkZoneChange = async (zoneId: string) => {
    if (selectedDeviceIds.length === 0) return;

    try {
      await Promise.all(selectedDeviceIds.map((id) => deviceApi.updateDevice(id, { zoneId })));
      showSnackbar(
        i18n.language === 'ar'
          ? `تم تحديث ${selectedDeviceIds.length} جهاز`
          : `Updated ${selectedDeviceIds.length} device(s)`,
        'success',
      );
      setSelectedDeviceIds([]);
      setSelectionMode(false);
      setShowBulkZoneModal(false);
      queryClient.invalidateQueries({ queryKey: deviceKeys.all });
    } catch (error) {
      const appError = parseError(error);
      logger.error('Bulk zone change failed:', error);
      showSnackbar(getErrorMessage(appError, i18n.language === 'ar' ? 'ar' : 'en'), 'error');
    }
  };

  // Advanced filtering and sorting
  const filteredDevices = useMemo(() => {
    let result = [...devices];

    // Status filter
    if (filterStatus !== 'all') {
      result = result.filter((device) => {
        const isOnline = isDeviceOnline(device);

        if (filterStatus === 'online') return isOnline;
        if (filterStatus === 'offline') return !isOnline;
        return true;
      });
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (device) =>
          device.name.toLowerCase().includes(query) ||
          device.type?.toLowerCase().includes(query) ||
          device.zone?.name?.toLowerCase().includes(query),
      );
    }

    // Device type filter
    if (selectedDeviceTypes.length > 0) {
      result = result.filter((device) => selectedDeviceTypes.includes(device.type));
    }

    // Zone filter
    if (selectedZones.length > 0) {
      result = result.filter((device) => device.zoneId && selectedZones.includes(device.zoneId));
    }

    // Battery filter
    if (batteryFilter !== 'all') {
      result = result.filter((device) => {
        if (device.battery === undefined) return false;
        if (batteryFilter === 'low') return device.battery < 20;
        if (batteryFilter === 'medium') return device.battery >= 20 && device.battery < 50;
        if (batteryFilter === 'high') return device.battery >= 50;
        return true;
      });
    }

    // Sorting
    result.sort((a, b) => {
      switch (sortBy) {
        case 'name-asc':
          return a.name.localeCompare(b.name);
        case 'name-desc':
          return b.name.localeCompare(a.name);
        case 'type':
          return (a.type || '').localeCompare(b.type || '');
        case 'status':
          const statusOrder: Record<string, number> = { online: 0, warning: 1, offline: 2 };
          return statusOrder[a.status] - statusOrder[b.status];
        case 'battery-low':
          return (a.battery || 100) - (b.battery || 100);
        case 'battery-high':
          return (b.battery || 0) - (a.battery || 0);
        case 'recent':
          return new Date(b.lastSeenAt || 0).getTime() - new Date(a.lastSeenAt || 0).getTime();
        default:
          return 0;
      }
    });

    return result;
  }, [
    devices,
    filterStatus,
    searchQuery,
    selectedDeviceTypes,
    selectedZones,
    batteryFilter,
    sortBy,
  ]);

  // ✅ P1-08: Memoize callback after filteredDevices is declared
  const selectAllDevices = useCallback(() => {
    setSelectedDeviceIds(filteredDevices.map((d) => d.id));
  }, [filteredDevices]);

  // Update nav header — switches between normal mode and selection mode UI
  useEffect(() => {
    const isArabic = i18n.language === 'ar';
    if (selectionMode) {
      const countLabel = isArabic
        ? `تم تحديد ${selectedDeviceIds.length}`
        : `${selectedDeviceIds.length} Selected`;
      navigation.setOptions({
        title: countLabel,
        headerRight: () => (
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 4,
              marginRight: isRTL ? 0 : 12,
              marginLeft: isRTL ? 12 : 0,
            }}
          >
            <TouchableOpacity
              onPress={selectAllDevices}
              style={{ padding: 6 }}
              accessibilityLabel={isArabic ? 'تحديد الكل' : 'Select all'}
            >
              <Ionicons name="checkmark-done-outline" size={22} color="#ffffff" />
            </TouchableOpacity>
            <TouchableOpacity
              onPress={toggleSelectionMode}
              style={{ padding: 6 }}
              accessibilityLabel={isArabic ? 'إلغاء التحديد' : 'Cancel selection'}
            >
              <Ionicons name="close" size={24} color="#ffffff" />
            </TouchableOpacity>
          </View>
        ),
      });
    } else {
      navigation.setOptions({
        title: isArabic ? 'الأجهزة' : 'Devices',
        headerRight: () => (
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 4,
              marginRight: isRTL ? 0 : 12,
              marginLeft: isRTL ? 12 : 0,
            }}
          >
            <TouchableOpacity onPress={() => setShowSearch((prev) => !prev)} style={{ padding: 6 }}>
              <Ionicons name="search" size={22} color="#ffffff" />
            </TouchableOpacity>
            {userCanManageDevices && (
              <TouchableOpacity
                onPress={toggleSelectionMode}
                style={{ padding: 6 }}
                accessibilityLabel={isArabic ? 'تحديد الأجهزة' : 'Select devices'}
              >
                <Ionicons name="checkmark-circle-outline" size={22} color="#ffffff" />
              </TouchableOpacity>
            )}
            {userCanManageDevices && (
              <TouchableOpacity
                onPress={() => navigation.navigate('AddDeviceWizard')}
                style={{ padding: 6 }}
              >
                <Ionicons name="add" size={24} color="#ffffff" />
              </TouchableOpacity>
            )}
          </View>
        ),
      });
    }
  }, [
    navigation,
    i18n.language,
    devices.length,
    selectionMode,
    selectedDeviceIds.length,
    selectAllDevices,
    toggleSelectionMode,
  ]);

  // ✅ P1-08: Memoize stats computation to avoid recalculation on every render
  const stats = useMemo(
    () => ({
      total: devices.length,
      online: devices.filter((d) => isDeviceOnline(d)).length,
      offline: devices.filter((d) => !isDeviceOnline(d)).length,
    }),
    [devices],
  );

  // ✅ P1-08: Memoize renderDevice callback to prevent re-creation on every render
  const renderDevice = useCallback(
    ({ item }: { item: Device }) => {
      const statusStyle = getStatusStyle(item);
      const isSelected = selectedDeviceIds.includes(item.id);

      // ✅ P1-06: Accessibility label with device info
      const deviceTypeLabel = item.deviceType
        ? getDeviceTypeLabel(item.deviceType as any, i18n.language === 'ar' ? 'ar' : 'en')
        : i18n.language === 'ar'
        ? 'حساس عام'
        : 'Generic Sensor';

      const isItemOnline = isDeviceOnline(item);
      const statusLabel = isItemOnline
        ? i18n.language === 'ar'
          ? 'متصل'
          : 'Online'
        : i18n.language === 'ar'
        ? 'غير متصل'
        : 'Offline';

      const effectiveLastSeenAt = getLastSeenTimestamp(item);

      const lastSeenLabel = effectiveLastSeenAt
        ? `${i18n.language === 'ar' ? 'آخر ظهور' : 'Last seen'} ${new Date(
            effectiveLastSeenAt,
          ).toLocaleString(i18n.language === 'ar' ? 'ar-SA' : 'en-US', {
            day: '2-digit',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit',
          })}`
        : i18n.language === 'ar'
        ? 'بدون وقت ظهور'
        : 'No last seen time';

      const accessibilityLabel = `${item.name}, ${deviceTypeLabel}, ${statusLabel}, ${lastSeenLabel}`;
      const accessibilityHint = selectionMode
        ? i18n.language === 'ar'
          ? 'انقر نقرًا مزدوجًا للتحديد أو إلغاء التحديد'
          : 'Double tap to select or deselect'
        : i18n.language === 'ar'
        ? 'انقر نقرًا مزدوجًا لعرض التفاصيل'
        : 'Double tap to view details';

      return (
        <TouchableOpacity
          style={[
            styles.deviceCard,
            { backgroundColor: theme.colors.card, borderColor: theme.colors.border },
            isSelected && {
              borderColor: theme.colors.primary,
              borderWidth: 2,
              backgroundColor: theme.colors.primary + '08',
            },
            !isItemOnline && { opacity: 0.75 },
          ]}
          activeOpacity={0.7}
          onPress={() => {
            if (selectionMode) {
              toggleDeviceSelection(item.id);
            } else {
              navigation.navigate('DeviceDetail', {
                deviceId: item.id,
                deviceName: item.name,
                deviceType: item.deviceType,
                hasBattery: item.hasBattery,
              });
            }
          }}
          onLongPress={() => {
            if (!selectionMode) {
              setSelectionMode(true);
              setSelectedDeviceIds([item.id]);
            } else {
              toggleDeviceSelection(item.id);
            }
          }}
          accessible={true}
          accessibilityLabel={accessibilityLabel}
          accessibilityHint={accessibilityHint}
          accessibilityRole="button"
          accessibilityState={{ selected: isSelected }}
        >
          {selectionMode && (
            <View style={styles.checkboxContainer}>
              <View
                style={[
                  styles.checkbox,
                  { borderColor: theme.colors.border },
                  isSelected && {
                    backgroundColor: theme.colors.primary,
                    borderColor: theme.colors.primary,
                  },
                ]}
              >
                {isSelected && <Ionicons name="checkmark" size={18} color="#ffffff" />}
              </View>
            </View>
          )}

          <View style={[styles.deviceHeader, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
            <View
              style={[styles.deviceIconContainer, { backgroundColor: theme.colors.primary + '20' }]}
            >
              <Ionicons name={getDeviceIcon(item.type)} size={28} color={theme.colors.primary} />
            </View>
            <View style={[styles.deviceInfo, { alignItems: isRTL ? 'flex-end' : 'flex-start' }]}>
              <Text
                style={[
                  styles.deviceName,
                  { color: theme.colors.text, textAlign: isRTL ? 'right' : 'left' },
                ]}
              >
                {item.name}
              </Text>
              <Text
                style={[
                  styles.deviceType,
                  { color: theme.colors.textSecondary, textAlign: isRTL ? 'right' : 'left' },
                ]}
              >
                {item.deviceType
                  ? getDeviceTypeLabel(item.deviceType as any, i18n.language === 'ar' ? 'ar' : 'en')
                  : i18n.language === 'ar'
                  ? 'حساس عام'
                  : 'Generic Sensor'}
              </Text>
              {item.zone && (
                <View
                  style={[styles.zoneContainer, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}
                >
                  <Ionicons name="location" size={14} color={theme.colors.textSecondary} />
                  <Text
                    style={[
                      styles.deviceZone,
                      { color: theme.colors.textSecondary, textAlign: isRTL ? 'right' : 'left' },
                    ]}
                  >
                    {item.zone.name}
                  </Text>
                </View>
              )}
            </View>
          </View>

          {/* القراءات الحية */}
          {item.latestReading && (
            <View
              style={[styles.readingsContainer, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}
            >
              {/* ✅ Motion Sensor Display (v2.4.0) */}
              {(() => {
                const deviceTypeLower = (item.deviceType || '').toLowerCase();
                const deviceNameLower = (item.name || '').toLowerCase();
                const isMotionDevice =
                  deviceTypeLower === 'motion' ||
                  deviceTypeLower.includes('motion') ||
                  deviceNameLower.includes('motion') ||
                  deviceNameLower.includes('حركة') ||
                  deviceNameLower.includes('pir');

                if (isMotionDevice) {
                  const now = new Date();
                  const lastDetection = item.latestReading?.ts
                    ? new Date(item.latestReading.ts)
                    : null;
                  const minutesSince = lastDetection
                    ? Math.floor((now.getTime() - lastDetection.getTime()) / 60000)
                    : null;
                  // Motion sensors send telemetry on detection — recent ts ≈ recent motion
                  const isActive =
                    item.status === 'online' && minutesSince !== null && minutesSince < 5;
                  const activityColor = isActive
                    ? theme.colors.error
                    : item.status === 'online'
                    ? theme.colors.success
                    : theme.colors.textSecondary;

                  return (
                    <View style={styles.motionReadingContainer}>
                      <View style={styles.readingItem}>
                        <Ionicons
                          name={isActive ? 'warning' : 'shield-checkmark-outline'}
                          size={18}
                          color={activityColor}
                        />
                        <Text
                          style={[
                            styles.readingValue,
                            { color: theme.colors.text, fontWeight: '700' },
                          ]}
                        >
                          {isActive
                            ? i18n.language === 'ar'
                              ? 'حركة مكتشفة'
                              : 'Motion Detected'
                            : i18n.language === 'ar'
                            ? 'لا حركة'
                            : 'No Motion'}
                        </Text>
                      </View>
                      {lastDetection && minutesSince !== null && (
                        <View style={styles.readingItem}>
                          <Ionicons name="time-outline" size={16} color={theme.colors.info} />
                          <Text
                            style={[
                              styles.readingValueSecondary,
                              { color: theme.colors.textSecondary },
                            ]}
                          >
                            {formatTimeSince(minutesSince, i18n.language === 'ar' ? 'ar' : 'en')}
                          </Text>
                        </View>
                      )}
                    </View>
                  );
                }
                return null;
              })()}

              {/* ✅ Door Sensor Display (v2.4.0) */}
              {(() => {
                const deviceTypeLower = (item.deviceType || '').toLowerCase();
                const deviceNameLower = (item.name || '').toLowerCase();
                const isDoorDevice =
                  deviceTypeLower === 'door' ||
                  deviceTypeLower.includes('door') ||
                  deviceNameLower.includes('door') ||
                  deviceNameLower.includes('باب') ||
                  deviceNameLower.includes('gate');

                if (isDoorDevice) {
                  // Derive door state from device status (warning → open/alarm, online → closed)
                  const currentState: DoorState =
                    item.status === 'warning'
                      ? 'open'
                      : item.status === 'offline'
                      ? ('unknown' as DoorState)
                      : 'closed';

                  const getStateColor = () => {
                    if (currentState === 'open') return theme.colors.error;
                    if (currentState === 'ajar') return theme.colors.warning;
                    if (currentState === ('unknown' as DoorState))
                      return theme.colors.textSecondary;
                    return theme.colors.success;
                  };

                  const getStateIcon = () => {
                    if (currentState === 'open') return 'lock-open';
                    if (currentState === 'ajar') return 'alert-circle';
                    if (currentState === ('unknown' as DoorState)) return 'help-circle-outline';
                    return 'lock-closed';
                  };

                  return (
                    <View style={styles.doorReadingContainer}>
                      <View style={styles.readingItem}>
                        <Ionicons name={getStateIcon()} size={18} color={getStateColor()} />
                        <Text
                          style={[
                            styles.readingValue,
                            { color: theme.colors.text, fontWeight: '700' },
                          ]}
                        >
                          {formatDoorState(currentState, i18n.language === 'ar' ? 'ar' : 'en')}
                        </Text>
                      </View>
                    </View>
                  );
                }
                return null;
              })()}

              {/* ⚙️ Actuator Device Display (v2.4.0) */}
              {(() => {
                const deviceTypeLower = (item.deviceType || '').toLowerCase();
                // ✅ FIX: Only show actuator control for ACTUATOR type devices
                // Don't show for SENSOR devices even if name contains control words
                const isActuatorDevice =
                  deviceTypeLower === 'actuator' ||
                  deviceTypeLower === 'relay' ||
                  deviceTypeLower === 'switch' ||
                  deviceTypeLower === 'valve' ||
                  deviceTypeLower === 'dimmer';

                if (isActuatorDevice) {
                  // Derive actuator state from device status
                  const derivedActuatorState: ActuatorState =
                    item.status === 'offline' ? 'off' : 'on';
                  const actuatorStatus = getActuatorStatus(
                    [], // events will come from API
                    derivedActuatorState,
                    item.status === 'online' ? 100 : 0, // position: 100% if on, 0% if off
                    undefined, // targetPosition
                    item.status !== 'offline', // automationEnabled
                  );

                  const getStateColor = (state: ActuatorState): string => {
                    switch (state) {
                      case 'on':
                        return '#10b981'; // green
                      case 'off':
                        return '#6b7280'; // gray
                      case 'intermediate':
                        return '#3b82f6'; // blue
                      case 'transitioning':
                        return '#f59e0b'; // orange
                      case 'error':
                        return '#ef4444'; // red
                      default:
                        return theme.colors.textSecondary;
                    }
                  };

                  const getStateIcon = (state: ActuatorState): string => {
                    switch (state) {
                      case 'on':
                        return 'toggle';
                      case 'off':
                        return 'toggle-outline';
                      case 'intermediate':
                        return 'options';
                      case 'transitioning':
                        return 'sync';
                      case 'error':
                        return 'alert-circle';
                      default:
                        return 'cog';
                    }
                  };

                  return (
                    <View style={styles.actuatorReadingContainer}>
                      {/* State Badge */}
                      <View style={styles.readingItem}>
                        <View
                          style={[
                            styles.actuatorStateBadge,
                            {
                              backgroundColor: `${getStateColor(actuatorStatus.currentState)}15`,
                              borderColor: getStateColor(actuatorStatus.currentState),
                            },
                          ]}
                        >
                          <Ionicons
                            name={getStateIcon(actuatorStatus.currentState) as any}
                            size={14}
                            color={getStateColor(actuatorStatus.currentState)}
                          />
                          <Text
                            style={[
                              styles.actuatorStateText,
                              { color: getStateColor(actuatorStatus.currentState) },
                            ]}
                          >
                            {formatActuatorState(
                              actuatorStatus.currentState,
                              isArabic ? 'ar' : 'en',
                            )}
                          </Text>
                        </View>
                      </View>

                      {/* Position Display */}
                      <View style={styles.readingItem}>
                        <Ionicons name="options-outline" size={16} color={theme.colors.primary} />
                        <Text style={[styles.readingValue, { color: theme.colors.text }]}>
                          {formatPosition(actuatorStatus.currentPosition, isArabic ? 'ar' : 'en')}
                        </Text>
                      </View>

                      {/* Today's Operations */}
                      <View style={styles.readingItem}>
                        <Ionicons name="git-commit-outline" size={16} color={theme.colors.info} />
                        <Text
                          style={[
                            styles.readingValueSecondary,
                            { color: theme.colors.textSecondary },
                          ]}
                        >
                          {formatOperationCount(
                            actuatorStatus.todayOperations,
                            isArabic ? 'ar' : 'en',
                          )}
                        </Text>
                      </View>

                      {/* Health Status Icon */}
                      {actuatorStatus.healthStatus !== 'good' && (
                        <View style={styles.readingItem}>
                          <Ionicons
                            name={
                              actuatorStatus.healthStatus === 'warning'
                                ? 'alert-circle'
                                : 'close-circle'
                            }
                            size={16}
                            color={
                              actuatorStatus.healthStatus === 'warning'
                                ? theme.colors.warning
                                : theme.colors.error
                            }
                          />
                        </View>
                      )}
                    </View>
                  );
                }
                return null;
              })()}

              {/* 🌬️ Air Quality Sensor Display (v2.4.0) */}
              {(() => {
                const deviceTypeLower = (item.deviceType || '').toLowerCase();
                const deviceNameLower = (item.name || '').toLowerCase();
                const isAirQualityDevice =
                  deviceTypeLower.includes('air') ||
                  deviceTypeLower.includes('quality') ||
                  deviceTypeLower.includes('co2') ||
                  deviceTypeLower.includes('voc') ||
                  deviceTypeLower.includes('pm2.5') ||
                  deviceTypeLower.includes('pm25') ||
                  deviceTypeLower.includes('aqi') ||
                  deviceNameLower.includes('air') ||
                  deviceNameLower.includes('quality') ||
                  deviceNameLower.includes('جودة') ||
                  deviceNameLower.includes('هواء');

                if (isAirQualityDevice) {
                  // Only show air quality data if real telemetry exists
                  if (!item.latestReading) return null;
                  const airQualityStatus = getAirQualityStatus([], 0, 0, 0);

                  const { currentCO2, currentVOC, currentPM25, currentAQI, overallLevel } =
                    airQualityStatus;

                  const getAQIBadgeColor = () => {
                    if (currentAQI <= 50) return '#10b981'; // Green
                    if (currentAQI <= 100) return '#84cc16'; // Lime
                    if (currentAQI <= 150) return '#f59e0b'; // Orange
                    return '#ef4444'; // Red
                  };

                  return (
                    <View style={styles.airQualityReadingContainer}>
                      {/* Overall AQI Badge */}
                      <View
                        style={[
                          styles.airQualityAqiBadge,
                          {
                            backgroundColor: getAQIBadgeColor() + '15',
                            borderColor: getAQIBadgeColor(),
                          },
                        ]}
                      >
                        <Ionicons name="cloudy" size={14} color={getAQIBadgeColor()} />
                        <Text style={[styles.airQualityAqiText, { color: getAQIBadgeColor() }]}>
                          {isArabic ? 'مؤشر' : 'AQI'}:{' '}
                          {formatAQI(currentAQI, isArabic ? 'ar' : 'en')}
                        </Text>
                      </View>

                      {/* CO2 Reading */}
                      <View style={styles.airQualityMeasurement}>
                        <Ionicons name="flask-outline" size={14} color={theme.colors.primary} />
                        <Text
                          style={[styles.airQualityMeasurementText, { color: theme.colors.text }]}
                        >
                          {formatCO2(currentCO2, isArabic ? 'ar' : 'en')}
                        </Text>
                      </View>

                      {/* VOC Reading */}
                      <View style={styles.airQualityMeasurement}>
                        <Ionicons name="water-outline" size={14} color={theme.colors.info} />
                        <Text
                          style={[styles.airQualityMeasurementText, { color: theme.colors.text }]}
                        >
                          {formatVOC(currentVOC, isArabic ? 'ar' : 'en')}
                        </Text>
                      </View>

                      {/* PM2.5 Reading */}
                      <View style={styles.airQualityMeasurement}>
                        <Ionicons name="ellipse-outline" size={14} color={theme.colors.warning} />
                        <Text
                          style={[styles.airQualityMeasurementText, { color: theme.colors.text }]}
                        >
                          {formatPM25(currentPM25, isArabic ? 'ar' : 'en')}
                        </Text>
                      </View>

                      {/* Health Warning (if needed) */}
                      {(currentAQI > 100 || currentCO2 > 1000) && (
                        <Ionicons name="alert-circle" size={16} color={theme.colors.error} />
                      )}
                    </View>
                  );
                }

                // 💡 Light Sensor Display
                const isLightDevice =
                  deviceTypeLower.includes('light') ||
                  deviceTypeLower.includes('illumin') ||
                  deviceTypeLower.includes('lux') ||
                  deviceTypeLower.includes('brightness') ||
                  deviceNameLower.includes('light') ||
                  deviceNameLower.includes('إضاءة') ||
                  deviceNameLower.includes('سطوع') ||
                  deviceNameLower.includes('لوكس');

                if (isLightDevice) {
                  // Mock data for testing
                  const lightStatus = getLightStatus([], 420);
                  const { currentLux, currentBrightness, isDaylight } = lightStatus;

                  return (
                    <View style={styles.lightReadingContainer}>
                      {/* Lux Badge */}
                      <View
                        style={[
                          styles.lightLuxBadge,
                          {
                            backgroundColor: getBrightnessColor(currentBrightness) + '15',
                            borderColor: getBrightnessColor(currentBrightness),
                          },
                        ]}
                      >
                        <Ionicons
                          name={isDaylight ? 'sunny' : 'moon'}
                          size={14}
                          color={getBrightnessColor(currentBrightness)}
                        />
                        <Text
                          style={[
                            styles.lightLuxText,
                            { color: getBrightnessColor(currentBrightness) },
                          ]}
                        >
                          {formatLux(currentLux, isArabic ? 'ar' : 'en')}
                        </Text>
                      </View>

                      {/* Brightness Level */}
                      <View style={styles.lightBrightnessInfo}>
                        <Text
                          style={[
                            styles.lightBrightnessText,
                            { color: theme.colors.textSecondary },
                          ]}
                        >
                          {formatBrightnessLevel(currentBrightness, isArabic ? 'ar' : 'en')}
                        </Text>
                      </View>
                    </View>
                  );
                }

                // 🌡️ Pressure Sensor Display
                const isPressureDevice =
                  deviceTypeLower.includes('pressure') ||
                  deviceTypeLower.includes('barometer') ||
                  deviceTypeLower.includes('atmospheric') ||
                  deviceTypeLower.includes('altitude') ||
                  deviceNameLower.includes('pressure') ||
                  deviceNameLower.includes('ضغط') ||
                  deviceNameLower.includes('باروميتر') ||
                  deviceNameLower.includes('جوي');

                if (isPressureDevice) {
                  // Use standard atmospheric pressure as default; 0 when offline
                  const pressureStatus = getPressureStatus(
                    [],
                    item.status === 'offline' ? 0 : 1013.25,
                  );
                  const { currentPressure, pressureLevel, weatherForecast } = pressureStatus;

                  return (
                    <View style={styles.pressureReadingContainer}>
                      {/* Pressure Badge */}
                      <View
                        style={[
                          styles.pressureBadge,
                          {
                            backgroundColor: getPressureLevelColor(pressureLevel) + '15',
                            borderColor: getPressureLevelColor(pressureLevel),
                          },
                        ]}
                      >
                        <Ionicons
                          name="speedometer-outline"
                          size={14}
                          color={getPressureLevelColor(pressureLevel)}
                        />
                        <Text
                          style={[
                            styles.pressureText,
                            { color: getPressureLevelColor(pressureLevel) },
                          ]}
                        >
                          {formatPressure(currentPressure, isArabic ? 'ar' : 'en')}
                        </Text>
                      </View>

                      {/* Weather Forecast Badge */}
                      <View
                        style={[
                          styles.pressureWeatherBadge,
                          {
                            backgroundColor: getWeatherForecastColor(weatherForecast) + '15',
                            borderColor: getWeatherForecastColor(weatherForecast),
                          },
                        ]}
                      >
                        <Ionicons
                          name={
                            weatherForecast === 'sunny'
                              ? 'sunny'
                              : weatherForecast === 'fair'
                              ? 'partly-sunny'
                              : weatherForecast === 'cloudy'
                              ? 'cloudy'
                              : weatherForecast === 'rainy'
                              ? 'rainy'
                              : 'thunderstorm'
                          }
                          size={14}
                          color={getWeatherForecastColor(weatherForecast)}
                        />
                        <Text
                          style={[
                            styles.pressureWeatherText,
                            { color: theme.colors.textSecondary },
                          ]}
                        >
                          {formatPressureLevel(pressureLevel, isArabic ? 'ar' : 'en')}
                        </Text>
                      </View>
                    </View>
                  );
                }

                // 🔥 Gas Sensor Display
                const isGasDevice =
                  deviceTypeLower.includes('gas') ||
                  deviceTypeLower.includes('smoke') ||
                  deviceTypeLower.includes('co') ||
                  deviceTypeLower.includes('ch4') ||
                  deviceTypeLower.includes('lpg') ||
                  deviceTypeLower.includes('h2s') ||
                  deviceTypeLower.includes('nh3') ||
                  deviceTypeLower.includes('voc') ||
                  deviceNameLower.includes('gas') ||
                  deviceNameLower.includes('غاز') ||
                  deviceNameLower.includes('تسرب') ||
                  deviceNameLower.includes('دخان');

                if (isGasDevice) {
                  // Use safe-level default when online; 0 when offline
                  const gasStatus = getGasStatus([], item.status === 'offline' ? 0 : 8, 'CO');
                  const { currentConcentration, toxicityLevel, leakInfo } = gasStatus;

                  return (
                    <View style={styles.gasReadingContainer}>
                      {/* Concentration Badge */}
                      <View
                        style={[
                          styles.gasConcentrationBadge,
                          {
                            backgroundColor: getToxicityLevelColor(toxicityLevel) + '15',
                            borderColor: getToxicityLevelColor(toxicityLevel),
                          },
                        ]}
                      >
                        <Ionicons
                          name="flame-outline"
                          size={14}
                          color={getToxicityLevelColor(toxicityLevel)}
                        />
                        <Text
                          style={[
                            styles.gasConcentrationText,
                            { color: getToxicityLevelColor(toxicityLevel) },
                          ]}
                        >
                          {formatConcentration(currentConcentration, isArabic ? 'ar' : 'en')}
                        </Text>
                      </View>

                      {/* Toxicity Level Badge */}
                      <View
                        style={[
                          styles.gasToxicityBadge,
                          { backgroundColor: getToxicityLevelColor(toxicityLevel) + '10' },
                        ]}
                      >
                        <Text
                          style={[styles.gasToxicityText, { color: theme.colors.textSecondary }]}
                        >
                          {formatToxicityLevel(toxicityLevel, isArabic ? 'ar' : 'en')}
                        </Text>
                      </View>

                      {/* Leak Warning */}
                      {leakInfo.isLeaking && (
                        <Ionicons name="alert-circle" size={16} color={theme.colors.error} />
                      )}
                    </View>
                  );
                }
                return null;
              })()}

              {/* 🔌 Power Outlet Display (v2.4.0) */}

              {/* Smoke Detector Device (v2.4.0) */}
              {(() => {
                const deviceTypeLower = (item.deviceType || '').toLowerCase();
                const deviceNameLower = (item.name || '').toLowerCase();
                const isSmokeDevice =
                  deviceTypeLower.includes('smoke') ||
                  deviceTypeLower.includes('fire') ||
                  deviceNameLower.includes('دخان') ||
                  deviceNameLower.includes('كاشف دخان') ||
                  deviceNameLower.includes('حريق') ||
                  deviceNameLower.includes('إنذار حريق');

                if (isSmokeDevice) {
                  // Only show smoke data if real telemetry exists
                  if (!item.latestReading) return null;
                  const smokeDensity =
                    item.status === 'warning' ? 25 : item.status === 'offline' ? 0 : 3.5;
                  const mockSmokeStatus = getSmokeStatus(
                    [] as SmokeReading[], // readings will come from API
                    smokeDensity,
                    undefined, // currentTemperature
                    undefined, // currentCO
                    i18n.language as 'en' | 'ar',
                  );

                  return (
                    <View style={styles.smokeReadingContainer}>
                      {/* Density Badge */}
                      <View
                        style={[
                          styles.smokeDensityBadge,
                          {
                            backgroundColor:
                              getDensityLevelColor(mockSmokeStatus.densityLevel) + '15',
                            borderColor: getDensityLevelColor(mockSmokeStatus.densityLevel),
                          },
                        ]}
                      >
                        <Ionicons
                          name="flame-outline"
                          size={14}
                          color={getDensityLevelColor(mockSmokeStatus.densityLevel)}
                        />
                        <Text
                          style={[
                            styles.smokeDensityText,
                            { color: getDensityLevelColor(mockSmokeStatus.densityLevel) },
                          ]}
                        >
                          {formatSmokeDensity(
                            mockSmokeStatus.density,
                            i18n.language as 'en' | 'ar',
                          )}
                        </Text>
                      </View>

                      {/* Density Level Badge */}
                      <View style={styles.smokeLevelBadge}>
                        <Text
                          style={[styles.smokeLevelText, { color: theme.colors.textSecondary }]}
                        >
                          {formatDensityLevel(
                            mockSmokeStatus.densityLevel,
                            i18n.language as 'en' | 'ar',
                          )}
                        </Text>
                      </View>

                      {/* Alarm Warning (Conditional) */}
                      {mockSmokeStatus.alarm.triggered && (
                        <Ionicons
                          name="alert-circle"
                          size={18}
                          color="#ef4444"
                          style={{ marginStart: 6 }}
                        />
                      )}
                    </View>
                  );
                }
                return null;
              })()}

              {/* 💧 Water Leak Sensor Display (v2.4.0) */}
              {(() => {
                const deviceTypeLower = (item.deviceType || '').toLowerCase();
                const deviceNameLower = (item.name || '').toLowerCase();
                const isWaterLeakDevice =
                  deviceTypeLower.includes('leak') ||
                  deviceTypeLower.includes('water') ||
                  deviceTypeLower.includes('flood') ||
                  deviceNameLower.includes('تسرب') ||
                  deviceNameLower.includes('ماء') ||
                  deviceNameLower.includes('فيضان') ||
                  deviceNameLower.includes('غمر');

                if (isWaterLeakDevice) {
                  // Only show water data if real telemetry exists
                  if (!item.latestReading) return null;
                  const waterLeakStatus = getWaterLeakStatus(
                    [] as WaterLeakReading[], // readings will come from API
                    item.status === 'warning' ? 15 : 0, // waterLevel: elevated if warning (leak)
                    item.status === 'warning' ? 35 : 2.5, // moisture: elevated if warning
                    i18n.language as 'en' | 'ar',
                  );

                  return (
                    <View style={styles.waterLeakReadingContainer}>
                      {/* Water Level Badge */}
                      <View
                        style={[
                          styles.waterLevelBadge,
                          { backgroundColor: getWaterStatusColor(waterLeakStatus.waterStatus) },
                        ]}
                      >
                        <Ionicons name="water-outline" size={14} color="#fff" />
                        <Text style={styles.waterLevelText}>
                          {formatWaterLevel(
                            waterLeakStatus.waterLevel,
                            i18n.language as 'en' | 'ar',
                          )}
                        </Text>
                      </View>

                      {/* Water Status Badge */}
                      <View
                        style={[
                          styles.waterSeverityBadge,
                          {
                            backgroundColor:
                              getWaterStatusColor(waterLeakStatus.waterStatus) + '20',
                          },
                        ]}
                      >
                        <Text
                          style={[
                            styles.waterSeverityText,
                            { color: getWaterStatusColor(waterLeakStatus.waterStatus) },
                          ]}
                        >
                          {formatWaterStatus(
                            waterLeakStatus.waterStatus,
                            i18n.language as 'en' | 'ar',
                          )}
                        </Text>
                      </View>

                      {/* Alarm Warning (Conditional) */}
                      {waterLeakStatus.currentLeak && (
                        <Ionicons
                          name="alert-circle"
                          size={18}
                          color="#ef4444"
                          style={{ marginStart: 6 }}
                        />
                      )}
                    </View>
                  );
                }
                return null;
              })()}
              {(() => {
                const deviceTypeLower = (item.deviceType || '').toLowerCase();
                const deviceNameLower = (item.name || '').toLowerCase();
                const isOutletDevice =
                  deviceTypeLower.includes('outlet') ||
                  deviceTypeLower.includes('socket') ||
                  deviceTypeLower.includes('plug') ||
                  deviceTypeLower.includes('switch') ||
                  deviceNameLower.includes('مقبس') ||
                  deviceNameLower.includes('قابس') ||
                  deviceNameLower.includes('مفتاح');

                if (isOutletDevice) {
                  // Only show outlet data if real telemetry exists
                  if (!item.latestReading) return null;
                  const derivedOutletState: OutletState =
                    item.status === 'offline' ? 'off' : item.status === 'warning' ? 'error' : 'on';
                  const outletStatus = getOutletStatus(
                    [], // events will come from API
                    derivedOutletState, // state from device status
                    0, // currentPowerW: unavailable without real telemetry
                    220, // voltage
                    0, // current: unavailable without real telemetry
                    undefined, // schedules
                    { pricePerKwh: 0.18, currency: 'SAR' },
                  );

                  const getStateColor = (state: OutletState): string => {
                    switch (state) {
                      case 'on':
                        return '#10b981'; // green
                      case 'off':
                        return '#6b7280'; // gray
                      case 'scheduled':
                        return '#3b82f6'; // blue
                      case 'error':
                        return '#ef4444'; // red
                      default:
                        return theme.colors.textSecondary;
                    }
                  };

                  const getStateIcon = (state: OutletState): string => {
                    switch (state) {
                      case 'on':
                        return 'flash';
                      case 'off':
                        return 'flash-off';
                      case 'scheduled':
                        return 'time';
                      case 'error':
                        return 'alert-circle';
                      default:
                        return 'power';
                    }
                  };

                  return (
                    <View style={styles.outletReadingContainer}>
                      {/* State Badge */}
                      <View style={styles.readingItem}>
                        <View
                          style={[
                            styles.outletStateBadge,
                            {
                              backgroundColor: `${getStateColor(outletStatus.currentState)}15`,
                              borderColor: getStateColor(outletStatus.currentState),
                            },
                          ]}
                        >
                          <Ionicons
                            name={getStateIcon(outletStatus.currentState) as any}
                            size={16}
                            color={getStateColor(outletStatus.currentState)}
                          />
                          <Text
                            style={[
                              styles.outletStateText,
                              { color: getStateColor(outletStatus.currentState) },
                            ]}
                          >
                            {formatOutletState(outletStatus.currentState, isArabic ? 'ar' : 'en')}
                          </Text>
                        </View>
                      </View>

                      {/* Power Display (if ON) */}
                      {outletStatus.isOn && (
                        <View style={styles.readingItem}>
                          <Ionicons name="flash" size={16} color={theme.colors.warning} />
                          <Text style={[styles.readingValue, { color: theme.colors.text }]}>
                            {formatPowerOutlet(outletStatus.currentPowerW, isArabic ? 'ar' : 'en')}
                          </Text>
                        </View>
                      )}

                      {/* Today's Cost */}
                      <View style={styles.readingItem}>
                        <Ionicons name="cash-outline" size={16} color={theme.colors.success} />
                        <Text style={[styles.readingValue, { color: theme.colors.text }]}>
                          {formatCostOutlet(outletStatus.todayCost, 'SAR', isArabic ? 'ar' : 'en')}
                        </Text>
                      </View>

                      {/* Uptime (if ON) */}
                      {outletStatus.isOn && outletStatus.uptimeMinutes > 0 && (
                        <View style={styles.readingItem}>
                          <Ionicons name="time-outline" size={16} color={theme.colors.info} />
                          <Text
                            style={[
                              styles.readingValueSecondary,
                              { color: theme.colors.textSecondary },
                            ]}
                          >
                            {formatDuration(outletStatus.uptimeMinutes, isArabic ? 'ar' : 'en')}
                          </Text>
                        </View>
                      )}
                    </View>
                  );
                }
                return null;
              })()}

              {/* ✅ Energy Meter Display (v2.4.0) */}
              {(() => {
                const deviceTypeLower = (item.deviceType || '').toLowerCase();
                const deviceNameLower = (item.name || '').toLowerCase();
                const isEnergyDevice =
                  deviceTypeLower === 'energy' ||
                  deviceTypeLower === 'power' ||
                  deviceTypeLower.includes('energy') ||
                  deviceTypeLower.includes('power') ||
                  deviceTypeLower.includes('meter') ||
                  deviceNameLower.includes('energy') ||
                  deviceNameLower.includes('power') ||
                  deviceNameLower.includes('meter') ||
                  deviceNameLower.includes('طاقة') ||
                  deviceNameLower.includes('كهرباء') ||
                  deviceNameLower.includes('عداد');

                if (isEnergyDevice) {
                  // Only show energy data if real telemetry exists
                  if (!item.latestReading) return null;
                  const mockEnergyEvents: EnergyEvent[] = [];
                  const tariffRate: TariffRate = { pricePerKwh: 0.18, currency: 'SAR' };

                  const energyStatus = getEnergyStatus(
                    mockEnergyEvents,
                    item.status === 'offline' ? 0 : 0, // Power: requires real telemetry
                    220, // Voltage: 220V
                    0, // Current: requires real telemetry
                    0.95, // Power factor: 0.95
                    50, // Frequency: 50Hz
                    tariffRate,
                  );

                  const todayCost = energyStatus.todayCost;

                  // Determine color based on power level
                  const getPowerColor = () => {
                    if (energyStatus.status === 'active') {
                      if (energyStatus.currentPowerW > 2000) return theme.colors.error;
                      if (energyStatus.currentPowerW > 1000) return theme.colors.warning;
                      return theme.colors.success;
                    }
                    return theme.colors.textSecondary;
                  };

                  const getPowerIcon = () => {
                    if (energyStatus.status === 'active') return 'flash';
                    if (energyStatus.status === 'idle') return 'flash-outline';
                    return 'power-outline';
                  };

                  return (
                    <View style={styles.energyReadingContainer}>
                      <View style={styles.readingItem}>
                        <Ionicons name={getPowerIcon()} size={18} color={getPowerColor()} />
                        <Text
                          style={[
                            styles.readingValue,
                            { color: theme.colors.text, fontWeight: '700' },
                          ]}
                        >
                          {formatPower(
                            energyStatus.currentPowerW,
                            i18n.language === 'ar' ? 'ar' : 'en',
                          )}
                        </Text>
                      </View>
                      <View style={styles.readingItem}>
                        <Text
                          style={[
                            styles.readingValueSecondary,
                            { color: theme.colors.textSecondary },
                          ]}
                        >
                          {formatCost(
                            todayCost,
                            tariffRate.currency,
                            i18n.language === 'ar' ? 'ar' : 'en',
                          )}
                        </Text>
                      </View>
                    </View>
                  );
                }
                return null;
              })()}

              {/* ✅ Camera Display (v2.4.0) */}
              {(() => {
                const deviceTypeLower = (item.deviceType || '').toLowerCase();
                const deviceNameLower = (item.name || '').toLowerCase();
                const isCameraDevice =
                  deviceTypeLower === 'camera' ||
                  deviceTypeLower.includes('camera') ||
                  deviceTypeLower.includes('cam') ||
                  deviceNameLower.includes('camera') ||
                  deviceNameLower.includes('cam') ||
                  deviceNameLower.includes('كاميرا');

                if (isCameraDevice) {
                  // Only show camera data if real telemetry exists
                  if (!item.latestReading) return null;
                  const mockCameraEvents: CameraEvent[] = [];
                  // Derive stream status from device status
                  const streamStatus = (
                    item.status === 'offline' ? 'offline' : 'idle'
                  ) as StreamStatus;

                  const cameraStatus = getCameraStatus(
                    mockCameraEvents,
                    streamStatus,
                    '1080p', // resolution
                    30, // fps
                    2500, // bitrate in kbps
                    0, // storageUsed: requires real data from camera API
                    100 * 1024 * 1024 * 1024, // 100GB total
                    item.battery,
                    80, // signal strength
                  );

                  const storagePercent = cameraStatus.storageUsedPercentage;
                  const resolution = cameraStatus.resolution;

                  // Determine color based on stream status
                  const getStreamColor = () => {
                    if (streamStatus === 'live') return theme.colors.success;
                    if (streamStatus === 'recording') return theme.colors.error;
                    if (streamStatus === 'idle') return theme.colors.textSecondary;
                    return theme.colors.textSecondary; // offline
                  };

                  const getStreamIcon = () => {
                    if (streamStatus === 'live') return 'videocam';
                    if (streamStatus === 'recording') return 'radio-button-on';
                    return 'videocam-outline';
                  };

                  return (
                    <View style={styles.cameraReadingContainer}>
                      <View style={styles.readingItem}>
                        <Ionicons name={getStreamIcon()} size={18} color={getStreamColor()} />
                        <Text
                          style={[
                            styles.readingValue,
                            { color: theme.colors.text, fontWeight: '700' },
                          ]}
                        >
                          {formatStreamStatus(streamStatus, i18n.language === 'ar' ? 'ar' : 'en')}
                        </Text>
                      </View>
                      <View style={styles.readingItem}>
                        <Text
                          style={[
                            styles.readingValueSecondary,
                            { color: theme.colors.textSecondary },
                          ]}
                        >
                          {resolution} • {storagePercent}%
                        </Text>
                      </View>
                    </View>
                  );
                }
                return null;
              })()}

              {item.latestReading.temperatureC !== null &&
                item.latestReading.temperatureC !== undefined && (
                  <View style={styles.readingItem}>
                    <Ionicons name="thermometer-outline" size={18} color={theme.colors.primary} />
                    <Text style={[styles.readingValue, { color: theme.colors.text }]}>
                      {item.latestReading.temperatureC.toFixed(1)}°C
                    </Text>
                  </View>
                )}
              {item.latestReading.humidityRh !== null &&
                item.latestReading.humidityRh !== undefined && (
                  <View style={styles.readingItem}>
                    <Ionicons name="water-outline" size={18} color={theme.colors.info} />
                    <Text style={[styles.readingValue, { color: theme.colors.text }]}>
                      {item.latestReading.humidityRh.toFixed(1)}%
                    </Text>
                  </View>
                )}
              {item.latestReading.vibrationMmS !== null &&
                item.latestReading.vibrationMmS !== undefined && (
                  <View style={styles.readingItem}>
                    <Ionicons name="pulse-outline" size={18} color={theme.colors.warning} />
                    <Text style={[styles.readingValue, { color: theme.colors.text }]}>
                      {item.latestReading.vibrationMmS.toFixed(1)} mm/s
                    </Text>
                  </View>
                )}
              {item.latestReading.powerKw !== null && item.latestReading.powerKw !== undefined && (
                <View style={styles.readingItem}>
                  <Ionicons name="flash-outline" size={18} color={theme.colors.warning} />
                  <Text style={[styles.readingValue, { color: theme.colors.text }]}>
                    {item.latestReading.powerKw.toFixed(2)} kW
                  </Text>
                </View>
              )}
              {item.latestReading.batteryPct !== null &&
                item.latestReading.batteryPct !== undefined && (
                  <View style={styles.readingItem}>
                    <Ionicons name="battery-half-outline" size={18} color={theme.colors.success} />
                    <Text style={[styles.readingValue, { color: theme.colors.text }]}>
                      {Math.round(item.latestReading.batteryPct)}%
                    </Text>
                  </View>
                )}
              {/* ✅ تحديث عرض مستوى الخزان - إظهار النسبة المئوية بدلاً من الارتفاع */}
              {item.latestReading.levelCm !== null &&
                item.latestReading.levelCm !== undefined &&
                (() => {
                  const deviceTypeLower = (item.deviceType || '').toLowerCase();
                  const deviceNameLower = (item.name || '').toLowerCase();
                  const isTankDevice =
                    deviceTypeLower.includes('water') ||
                    deviceTypeLower.includes('diesel') ||
                    deviceTypeLower.includes('gas') ||
                    deviceTypeLower.includes('tank') ||
                    deviceTypeLower.includes('level') ||
                    deviceNameLower.includes('water') ||
                    deviceNameLower.includes('diesel') ||
                    deviceNameLower.includes('gas') ||
                    deviceNameLower.includes('tank');

                  // إذا كان خزان ولديه بيانات السعة والارتفاع الكلي - عرض النسبة والحجم
                  if (isTankDevice && item.tankCapacityLiters && item.tankHeightCm) {
                    const percentage = calculatePercentage(
                      item.latestReading.levelCm,
                      item.tankHeightCm,
                    );
                    const remainingVolume = calculateRemainingVolume(
                      item.latestReading.levelCm,
                      item.tankHeightCm,
                      item.tankCapacityLiters,
                    );

                    // تحديد اللون حسب النسبة
                    const getPercentageColor = (pct: number) => {
                      if (pct > 75) return theme.colors.success;
                      if (pct > 25) return theme.colors.warning;
                      return theme.colors.error;
                    };

                    return (
                      <View style={styles.tankReadingContainer}>
                        <View style={styles.readingItem}>
                          <Ionicons name="water" size={18} color={getPercentageColor(percentage)} />
                          <Text
                            style={[
                              styles.readingValue,
                              { color: theme.colors.text, fontWeight: '700' },
                            ]}
                          >
                            {formatPercentage(percentage)}
                          </Text>
                        </View>
                        <View style={styles.readingItem}>
                          <Ionicons name="cube-outline" size={16} color={theme.colors.info} />
                          <Text
                            style={[
                              styles.readingValueSecondary,
                              { color: theme.colors.textSecondary },
                            ]}
                          >
                            {formatVolume(remainingVolume, 'L')}{' '}
                            {i18n.language === 'ar' ? 'لتر' : 'L'}
                          </Text>
                        </View>
                      </View>
                    );
                  }

                  // إذا لم يكن خزان أو ليس لديه بيانات - عرض الارتفاع كما هو
                  return (
                    <View style={styles.readingItem}>
                      <Ionicons name="speedometer-outline" size={18} color={theme.colors.warning} />
                      <Text style={[styles.readingValue, { color: theme.colors.text }]}>
                        {item.latestReading.levelCm.toFixed(1)} cm
                      </Text>
                    </View>
                  );
                })()}
              {item.latestReading.rssiDbm !== null && item.latestReading.rssiDbm !== undefined && (
                <View style={styles.readingItem}>
                  <Ionicons
                    name={
                      item.latestReading.rssiDbm >= -60
                        ? 'wifi'
                        : item.latestReading.rssiDbm >= -80
                        ? 'wifi'
                        : 'wifi-outline'
                    }
                    size={18}
                    color={
                      item.latestReading.rssiDbm >= -60
                        ? theme.colors.success
                        : item.latestReading.rssiDbm >= -80
                        ? theme.colors.warning
                        : theme.colors.error
                    }
                  />
                  <Text
                    style={[
                      styles.readingValue,
                      {
                        color:
                          item.latestReading.rssiDbm >= -60
                            ? theme.colors.success
                            : item.latestReading.rssiDbm >= -80
                            ? theme.colors.warning
                            : theme.colors.error,
                      },
                    ]}
                  >
                    {item.latestReading.rssiDbm} {i18n.language === 'ar' ? 'ديسيبل' : 'dBm'}
                  </Text>
                </View>
              )}
            </View>
          )}

          <View style={[styles.deviceFooter, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
            <View
              style={[styles.deviceFooterLeft, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}
            >
              <View
                style={[
                  styles.statusBadge,
                  { backgroundColor: statusStyle.bg, flexDirection: isRTL ? 'row-reverse' : 'row' },
                ]}
              >
                <Ionicons name={statusStyle.icon} size={14} color={statusStyle.text} />
                <Text style={[styles.statusText, { color: statusStyle.text }]}>
                  {statusStyle.label}
                </Text>
              </View>

              {effectiveLastSeenAt && (
                <Text
                  style={[
                    styles.lastSeen,
                    { color: theme.colors.textSecondary, textAlign: isRTL ? 'right' : 'left' },
                  ]}
                >
                  {i18n.language === 'ar' ? 'آخر ظهور: ' : 'Last seen: '}
                  {new Date(effectiveLastSeenAt).toLocaleString(
                    i18n.language === 'ar' ? 'ar-SA' : 'en-US',
                    {
                      day: '2-digit',
                      month: 'short',
                      hour: '2-digit',
                      minute: '2-digit',
                    },
                  )}
                </Text>
              )}
            </View>

            {!selectionMode && (
              <Ionicons
                name={isRTL ? 'chevron-back' : 'chevron-forward'}
                size={20}
                color={theme.colors.textSecondary}
              />
            )}
          </View>
        </TouchableOpacity>
      );
    },
    [
      selectedDeviceIds,
      selectionMode,
      theme.colors,
      i18n.language,
      toggleDeviceSelection,
      navigation,
    ],
  ); // ✅ P1-08: useCallback dependencies

  // P1-03: Explicit state rendering
  if (isLoading && !isRefetching) {
    return (
      <LoadingScreen
        message={i18n.language === 'ar' ? 'جاري تحميل الأجهزة...' : 'Loading devices...'}
      />
    );
  }

  // Only show full error screen when there is NO cached data to display.
  // If we have cached devices from a previous fetch, keep showing them even
  // if the background refetch failed (e.g. brief WiFi→5G network switch).
  if (error && rawDevices.length === 0) {
    return (
      <ErrorView
        message={error}
        onRetry={() => refetch()}
        icon="cube-outline"
        title={i18n.language === 'ar' ? 'فشل تحميل الأجهزة' : 'Failed to Load Devices'}
      />
    );
  }

  // P1-03: Empty state for no devices
  if (devices.length === 0 && !isLoading && !isRefetching && !error) {
    return (
      <EmptyState
        icon="cube-outline"
        title={i18n.language === 'ar' ? 'لا توجد أجهزة' : 'No Devices'}
        message={
          i18n.language === 'ar' ? 'لم يتم إضافة أي أجهزة بعد.' : 'No devices have been added yet.'
        }
        actionLabel={
          userCanManageDevices ? (i18n.language === 'ar' ? 'إضافة جهاز' : 'Add Device') : undefined
        }
        onAction={userCanManageDevices ? () => navigation.navigate('AddDeviceWizard') : undefined}
      />
    );
  }

  return (
    <View
      style={[
        styles.container,
        { backgroundColor: theme.colors.background, paddingTop: headerHeight },
      ]}
    >
      {/* Stats banner — shown in both normal and selection mode */}
      <View
        style={[
          styles.statsBanner,
          { backgroundColor: theme.colors.card, borderBottomColor: theme.colors.border },
        ]}
      >
        {selectionMode ? (
          <View style={styles.selectionRow}>
            <TouchableOpacity onPress={toggleSelectionMode} style={styles.selectionHeaderButton}>
              <Ionicons name="close" size={22} color={theme.colors.text} />
            </TouchableOpacity>
            <Text style={[styles.selectionHeaderText, { color: theme.colors.text }]}>
              {i18n.language === 'ar'
                ? `${selectedDeviceIds.length} محدد`
                : `${selectedDeviceIds.length} selected`}
            </Text>
            <TouchableOpacity
              onPress={
                selectedDeviceIds.length === filteredDevices.length
                  ? deselectAllDevices
                  : selectAllDevices
              }
              style={styles.selectionHeaderButton}
            >
              <Text style={[styles.selectionHeaderButtonText, { color: theme.colors.primary }]}>
                {selectedDeviceIds.length === filteredDevices.length
                  ? i18n.language === 'ar'
                    ? 'إلغاء الكل'
                    : 'Deselect All'
                  : i18n.language === 'ar'
                  ? 'تحديد الكل'
                  : 'Select All'}
              </Text>
            </TouchableOpacity>
          </View>
        ) : (
          <View style={styles.kpiRow}>
            <View
              style={[
                styles.kpiChip,
                {
                  backgroundColor: theme.colors.success + '15',
                  borderColor: theme.colors.success + '40',
                },
              ]}
            >
              <Ionicons name="radio-button-on" size={10} color={theme.colors.success} />
              <Text style={[styles.kpiValue, { color: theme.colors.success }]}>{stats.online}</Text>
              <Text style={[styles.kpiLabel, { color: theme.colors.textSecondary }]}>
                {i18n.language === 'ar' ? 'متصل' : 'online'}
              </Text>
            </View>
            {stats.offline > 0 && (
              <View
                style={[
                  styles.kpiChip,
                  {
                    backgroundColor: theme.colors.error + '15',
                    borderColor: theme.colors.error + '40',
                  },
                ]}
              >
                <Ionicons name="radio-button-off" size={10} color={theme.colors.error} />
                <Text style={[styles.kpiValue, { color: theme.colors.error }]}>
                  {stats.offline}
                </Text>
                <Text style={[styles.kpiLabel, { color: theme.colors.textSecondary }]}>
                  {i18n.language === 'ar' ? 'غير متصل' : 'offline'}
                </Text>
              </View>
            )}
            <View
              style={[
                styles.kpiChip,
                {
                  backgroundColor: theme.colors.primary + '10',
                  borderColor: theme.colors.primary + '30',
                },
              ]}
            >
              <Ionicons name="hardware-chip-outline" size={12} color={theme.colors.primary} />
              <Text style={[styles.kpiValue, { color: theme.colors.primary }]}>{stats.total}</Text>
              <Text style={[styles.kpiLabel, { color: theme.colors.textSecondary }]}>
                {i18n.language === 'ar' ? 'إجمالي' : 'total'}
              </Text>
            </View>
          </View>
        )}
      </View>

      {/* Collapsible Search Bar */}
      {showSearch && (
        <View style={[styles.searchContainer, { backgroundColor: theme.colors.card }]}>
          <View
            style={[
              styles.searchBar,
              { backgroundColor: theme.colors.background, borderColor: theme.colors.border },
            ]}
          >
            <Ionicons name="search" size={18} color={theme.colors.textSecondary} />
            <TextInput
              autoFocus
              style={[styles.searchInput, { color: theme.colors.text }]}
              placeholder={i18n.language === 'ar' ? 'ابحث...' : 'Search...'}
              placeholderTextColor={theme.colors.textSecondary}
              value={searchQuery}
              onChangeText={setSearchQuery}
            />
            {searchQuery.length > 0 ? (
              <TouchableOpacity onPress={() => setSearchQuery('')} style={styles.clearSearchButton}>
                <Ionicons name="close-circle" size={18} color={theme.colors.textSecondary} />
              </TouchableOpacity>
            ) : (
              <TouchableOpacity onPress={openFilters} style={styles.inlineFilterButton}>
                <Ionicons name="options-outline" size={16} color={theme.colors.primary} />
                {activeFiltersCount > 0 && (
                  <View style={styles.filtersBadgeSmall}>
                    <Text style={styles.filtersBadgeText}>{activeFiltersCount}</Text>
                  </View>
                )}
              </TouchableOpacity>
            )}
          </View>
        </View>
      )}

      {/* Active Filters Chips */}
      {activeFiltersCount > 0 && (
        <View style={[styles.activeFiltersContainer, { backgroundColor: theme.colors.card }]}>
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.activeFiltersScroll}
          >
            {selectedDeviceTypes.map((type) => (
              <View
                key={type}
                style={[
                  styles.filterChip,
                  {
                    backgroundColor: theme.colors.primary + '20',
                    borderColor: theme.colors.primary,
                  },
                ]}
              >
                <Text style={[styles.filterChipText, { color: theme.colors.primary }]}>{type}</Text>
                <TouchableOpacity
                  onPress={() => setSelectedDeviceTypes((prev) => prev.filter((t) => t !== type))}
                >
                  <Ionicons name="close" size={16} color={theme.colors.primary} />
                </TouchableOpacity>
              </View>
            ))}
            {selectedZones.map((zoneId) => {
              const zone = zones.find((z) => z.id === zoneId);
              return zone ? (
                <View
                  key={zoneId}
                  style={[
                    styles.filterChip,
                    { backgroundColor: theme.colors.info + '20', borderColor: theme.colors.info },
                  ]}
                >
                  <Text style={[styles.filterChipText, { color: theme.colors.info }]}>
                    {zone.name}
                  </Text>
                  <TouchableOpacity
                    onPress={() => setSelectedZones((prev) => prev.filter((z) => z !== zoneId))}
                  >
                    <Ionicons name="close" size={16} color={theme.colors.info} />
                  </TouchableOpacity>
                </View>
              ) : null;
            })}
            {batteryFilter !== 'all' && (
              <View
                style={[
                  styles.filterChip,
                  {
                    backgroundColor: theme.colors.warning + '20',
                    borderColor: theme.colors.warning,
                  },
                ]}
              >
                <Text style={[styles.filterChipText, { color: theme.colors.warning }]}>
                  {i18n.language === 'ar'
                    ? `بطارية: ${
                        batteryFilter === 'low'
                          ? 'منخفضة'
                          : batteryFilter === 'medium'
                          ? 'متوسطة'
                          : 'عالية'
                      }`
                    : `Battery: ${batteryFilter}`}
                </Text>
                <TouchableOpacity onPress={() => setBatteryFilter('all')}>
                  <Ionicons name="close" size={16} color={theme.colors.warning} />
                </TouchableOpacity>
              </View>
            )}
            <TouchableOpacity onPress={clearAllFilters}>
              <Text style={[styles.clearFiltersText, { color: theme.colors.error }]}>
                {i18n.language === 'ar' ? 'مسح الكل' : 'Clear All'}
              </Text>
            </TouchableOpacity>
          </ScrollView>
        </View>
      )}

      {/* Clean Filter Tabs Row */}
      <View
        style={[
          styles.filterTabsContainer,
          { backgroundColor: theme.colors.card, borderTopColor: theme.colors.border },
        ]}
      >
        <TouchableOpacity
          style={[
            styles.filterTab,
            filterStatus === 'all' && {
              backgroundColor: theme.colors.primary + '15',
              borderBottomWidth: 2,
              borderBottomColor: theme.colors.primary,
            },
          ]}
          onPress={() => setFilterStatus('all')}
        >
          <Text
            style={[
              styles.filterTabText,
              { color: theme.colors.text },
              filterStatus === 'all' && { color: theme.colors.primary, fontWeight: '600' },
            ]}
          >
            {i18n.language === 'ar' ? 'الكل' : 'All'}
          </Text>
          <Text style={[styles.filterTabCount, { color: theme.colors.textSecondary }]}>
            ({devices.length})
          </Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[
            styles.filterTab,
            filterStatus === 'online' && {
              backgroundColor: theme.colors.success + '10',
              borderBottomWidth: 2,
              borderBottomColor: theme.colors.success,
            },
          ]}
          onPress={() => setFilterStatus('online')}
        >
          <Text
            style={[
              styles.filterTabText,
              { color: theme.colors.text },
              filterStatus === 'online' && { color: theme.colors.success, fontWeight: '600' },
            ]}
          >
            {i18n.language === 'ar' ? 'متصل' : 'Online'}
          </Text>
          <Text style={[styles.filterTabCount, { color: theme.colors.textSecondary }]}>
            ({stats.online})
          </Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[
            styles.filterTab,
            filterStatus === 'offline' && {
              backgroundColor: theme.colors.error + '10',
              borderBottomWidth: 2,
              borderBottomColor: theme.colors.error,
            },
          ]}
          onPress={() => setFilterStatus('offline')}
        >
          <Text
            style={[
              styles.filterTabText,
              { color: theme.colors.text },
              filterStatus === 'offline' && { color: theme.colors.error, fontWeight: '600' },
            ]}
          >
            {i18n.language === 'ar' ? 'غير متصل' : 'Offline'}
          </Text>
          <Text style={[styles.filterTabCount, { color: theme.colors.textSecondary }]}>
            ({stats.offline})
          </Text>
        </TouchableOpacity>

        {devices.some((d) => d.status === 'warning') && (
          <TouchableOpacity
            style={[
              styles.filterTab,
              filterStatus === 'warning' && {
                backgroundColor: theme.colors.warning + '10',
                borderBottomWidth: 2,
                borderBottomColor: theme.colors.warning,
              },
            ]}
            onPress={() => setFilterStatus('warning' as any)}
          >
            <Text
              style={[
                styles.filterTabText,
                { color: theme.colors.text },
                filterStatus === 'warning' && { color: theme.colors.warning, fontWeight: '600' },
              ]}
            >
              {i18n.language === 'ar' ? 'يحتاج انتباه' : 'Needs Attention'}
            </Text>
          </TouchableOpacity>
        )}
      </View>

      {/* Quick Battery Filter */}
      <View style={[styles.quickFiltersContainer, { backgroundColor: theme.colors.card }]}>
        <TouchableOpacity
          style={[
            styles.quickFilterChip,
            { borderColor: theme.colors.warning },
            batteryFilter === 'low' && { backgroundColor: theme.colors.warning + '15' },
          ]}
          onPress={() => setBatteryFilter((prev) => (prev === 'low' ? 'all' : 'low'))}
          accessibilityLabel={
            i18n.language === 'ar' ? 'تبديل فلتر البطارية المنخفضة' : 'Toggle low battery filter'
          }
        >
          <Ionicons name="battery-dead-outline" size={14} color={theme.colors.warning} />
          <Text style={[styles.quickFilterText, { color: theme.colors.warning }]}>
            {i18n.language === 'ar' ? 'بطارية منخفضة' : 'Low Battery'}
          </Text>
        </TouchableOpacity>
      </View>

      {/* Devices List */}
      <FlatList
        data={filteredDevices}
        renderItem={renderDevice}
        keyExtractor={(item) => item.id}
        contentContainerStyle={styles.listContent}
        // ✅ P1-08: Performance optimizations for smooth scrolling
        removeClippedSubviews={Platform.OS === 'android'} // Android optimization
        initialNumToRender={15} // Render enough to fill screen + buffer
        maxToRenderPerBatch={10} // Batch size for rendering
        windowSize={21} // Number of items to render outside visible area
        updateCellsBatchingPeriod={50} // Batch UI updates every 50ms
        refreshControl={
          <RefreshControl
            refreshing={isRefetching}
            onRefresh={onRefresh}
            colors={[theme.colors.primary]}
          />
        }
        ListEmptyComponent={
          <View style={styles.emptyState}>
            <View
              style={[styles.emptyIconContainer, { backgroundColor: theme.colors.primary + '10' }]}
            >
              <Ionicons name="hardware-chip-outline" size={64} color={theme.colors.textSecondary} />
            </View>
            <Text style={[styles.emptyText, { color: theme.colors.text }]}>
              {i18n.language === 'ar' ? 'لا توجد أجهزة' : 'No devices found'}
            </Text>
            <Text style={[styles.emptySubtext, { color: theme.colors.textSecondary }]}>
              {searchQuery || activeFiltersCount > 0
                ? i18n.language === 'ar'
                  ? 'حاول تعديل البحث أو الفلاتر'
                  : 'Try adjusting your search or filters'
                : i18n.language === 'ar'
                ? 'اسحب للأسفل للتحديث'
                : 'Pull down to refresh'}
            </Text>
            {activeFiltersCount > 0 && (
              <TouchableOpacity
                style={[styles.clearFiltersButton, { backgroundColor: theme.colors.primary }]}
                onPress={clearAllFilters}
              >
                <Text style={styles.clearFiltersButtonText}>
                  {i18n.language === 'ar' ? 'مسح الفلاتر' : 'Clear Filters'}
                </Text>
              </TouchableOpacity>
            )}
            {!searchQuery && activeFiltersCount === 0 && userCanManageDevices && (
              <TouchableOpacity
                style={[styles.clearFiltersButton, { backgroundColor: theme.colors.primary }]}
                onPress={() => navigation.navigate('AddDeviceWizard')}
                accessibilityLabel={i18n.language === 'ar' ? 'إضافة جهاز جديد' : 'Add new device'}
              >
                <Text style={styles.clearFiltersButtonText}>
                  {i18n.language === 'ar' ? 'إضافة جهاز' : 'Add Device'}
                </Text>
              </TouchableOpacity>
            )}
          </View>
        }
      />

      {/* FAB - Floating Action Button or Bulk Actions Bar */}
      {selectionMode && selectedDeviceIds.length > 0 ? (
        <View
          style={[
            styles.bulkActionsBar,
            { backgroundColor: theme.colors.card, borderTopColor: theme.colors.border },
          ]}
        >
          <TouchableOpacity
            style={[styles.bulkActionButton, { backgroundColor: theme.colors.error }]}
            onPress={handleBulkDelete}
          >
            <Ionicons name="trash-outline" size={20} color="#ffffff" />
            <Text style={styles.bulkActionButtonText}>
              {i18n.language === 'ar' ? 'حذف' : 'Delete'}
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.bulkActionButton, { backgroundColor: theme.colors.info }]}
            onPress={openBulkZone}
          >
            <Ionicons name="location-outline" size={20} color="#ffffff" />
            <Text style={styles.bulkActionButtonText}>
              {i18n.language === 'ar' ? 'نقل للمنطقة' : 'Move Zone'}
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.bulkActionButton, { backgroundColor: theme.colors.textSecondary }]}
            onPress={() => {
              setSelectionMode(false);
              setSelectedDeviceIds([]);
            }}
          >
            <Ionicons name="close" size={20} color="#ffffff" />
            <Text style={styles.bulkActionButtonText}>
              {i18n.language === 'ar' ? 'إلغاء' : 'Cancel'}
            </Text>
          </TouchableOpacity>
        </View>
      ) : null}

      {/* Snackbar */}
      {snackbar.visible && (
        <View
          style={[
            styles.snackbar,
            {
              backgroundColor: snackbar.type === 'success' ? '#16a34a' : '#dc2626',
              bottom: Math.max(16, insets.bottom + 8),
            },
          ]}
          accessibilityRole="alert"
        >
          <Text style={styles.snackbarText}>{snackbar.message}</Text>
        </View>
      )}

      {/* Filters Modal */}
      <Modal
        visible={showFiltersModal}
        animationType="slide"
        transparent={true}
        onRequestClose={() => setShowFiltersModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, { backgroundColor: theme.colors.card }]}>
            {/* Modal Header */}
            <View style={[styles.modalHeader, { borderBottomColor: theme.colors.border }]}>
              <Text style={[styles.modalTitle, { color: theme.colors.text }]}>
                {i18n.language === 'ar' ? 'الفلاتر والترتيب' : 'Filters & Sort'}
              </Text>
              <TouchableOpacity onPress={() => setShowFiltersModal(false)}>
                <Ionicons name="close" size={28} color={theme.colors.text} />
              </TouchableOpacity>
            </View>

            <ScrollView style={styles.modalBody} showsVerticalScrollIndicator={false}>
              {/* Device Types Section */}
              <View style={styles.filterSection}>
                <Text style={[styles.filterSectionTitle, { color: theme.colors.text }]}>
                  {i18n.language === 'ar' ? 'نوع الجهاز' : 'Device Type'}
                </Text>
                <View style={styles.chipsContainer}>
                  {deviceTypes.map((type) => (
                    <TouchableOpacity
                      key={type}
                      style={[
                        styles.chip,
                        {
                          backgroundColor: theme.colors.background,
                          borderColor: theme.colors.border,
                        },
                        selectedDeviceTypes.includes(type) && {
                          backgroundColor: theme.colors.primary,
                          borderColor: theme.colors.primary,
                        },
                      ]}
                      onPress={() => {
                        if (selectedDeviceTypes.includes(type)) {
                          setSelectedDeviceTypes((prev) => prev.filter((t) => t !== type));
                        } else {
                          setSelectedDeviceTypes((prev) => [...prev, type]);
                        }
                      }}
                    >
                      <Text
                        style={[
                          styles.chipText,
                          { color: theme.colors.text },
                          selectedDeviceTypes.includes(type) && { color: '#ffffff' },
                        ]}
                      >
                        {type}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>

              {/* Zones Section */}
              {zones.length > 0 && (
                <View style={styles.filterSection}>
                  <Text style={[styles.filterSectionTitle, { color: theme.colors.text }]}>
                    {i18n.language === 'ar' ? 'المنطقة' : 'Zone'}
                  </Text>
                  <View style={styles.chipsContainer}>
                    {zones.map((zone) => (
                      <TouchableOpacity
                        key={zone.id}
                        style={[
                          styles.chip,
                          {
                            backgroundColor: theme.colors.background,
                            borderColor: theme.colors.border,
                          },
                          selectedZones.includes(zone.id) && {
                            backgroundColor: theme.colors.info,
                            borderColor: theme.colors.info,
                          },
                        ]}
                        onPress={() => {
                          if (selectedZones.includes(zone.id)) {
                            setSelectedZones((prev) => prev.filter((z) => z !== zone.id));
                          } else {
                            setSelectedZones((prev) => [...prev, zone.id]);
                          }
                        }}
                      >
                        <Text
                          style={[
                            styles.chipText,
                            { color: theme.colors.text },
                            selectedZones.includes(zone.id) && { color: '#ffffff' },
                          ]}
                        >
                          {zone.name}
                        </Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                </View>
              )}

              {/* Battery Filter Section */}
              <View style={styles.filterSection}>
                <Text style={[styles.filterSectionTitle, { color: theme.colors.text }]}>
                  {i18n.language === 'ar' ? 'مستوى البطارية' : 'Battery Level'}
                </Text>
                <View style={styles.chipsContainer}>
                  {[
                    {
                      value: 'all',
                      label: i18n.language === 'ar' ? 'الكل' : 'All',
                      icon: 'battery-full',
                    },
                    {
                      value: 'low',
                      label: i18n.language === 'ar' ? 'منخفضة (<20%)' : 'Low (<20%)',
                      icon: 'battery-dead',
                    },
                    {
                      value: 'medium',
                      label: i18n.language === 'ar' ? 'متوسطة (20-50%)' : 'Medium (20-50%)',
                      icon: 'battery-half',
                    },
                    {
                      value: 'high',
                      label: i18n.language === 'ar' ? 'عالية (>50%)' : 'High (>50%)',
                      icon: 'battery-charging',
                    },
                  ].map((option) => (
                    <TouchableOpacity
                      key={option.value}
                      style={[
                        styles.chip,
                        {
                          backgroundColor: theme.colors.background,
                          borderColor: theme.colors.border,
                        },
                        batteryFilter === option.value && {
                          backgroundColor: theme.colors.warning,
                          borderColor: theme.colors.warning,
                        },
                      ]}
                      onPress={() => setBatteryFilter(option.value as any)}
                    >
                      <Ionicons
                        name={option.icon as any}
                        size={16}
                        color={batteryFilter === option.value ? '#ffffff' : theme.colors.text}
                      />
                      <Text
                        style={[
                          styles.chipText,
                          { color: theme.colors.text },
                          batteryFilter === option.value && { color: '#ffffff' },
                        ]}
                      >
                        {option.label}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>

              {/* Sort Section */}
              <View style={styles.filterSection}>
                <Text style={[styles.filterSectionTitle, { color: theme.colors.text }]}>
                  {i18n.language === 'ar' ? 'الترتيب' : 'Sort By'}
                </Text>
                <View style={styles.sortOptionsContainer}>
                  {[
                    {
                      value: 'name-asc',
                      label: i18n.language === 'ar' ? 'الاسم (أ-ي)' : 'Name (A-Z)',
                      icon: 'arrow-up',
                    },
                    {
                      value: 'name-desc',
                      label: i18n.language === 'ar' ? 'الاسم (ي-أ)' : 'Name (Z-A)',
                      icon: 'arrow-down',
                    },
                    {
                      value: 'type',
                      label: i18n.language === 'ar' ? 'النوع' : 'Type',
                      icon: 'list',
                    },
                    {
                      value: 'status',
                      label: i18n.language === 'ar' ? 'الحالة' : 'Status',
                      icon: 'pulse',
                    },
                    {
                      value: 'battery-low',
                      label: i18n.language === 'ar' ? 'البطارية (أقل)' : 'Battery (Low)',
                      icon: 'battery-dead',
                    },
                    {
                      value: 'battery-high',
                      label: i18n.language === 'ar' ? 'البطارية (أعلى)' : 'Battery (High)',
                      icon: 'battery-charging',
                    },
                    {
                      value: 'recent',
                      label: i18n.language === 'ar' ? 'الأحدث' : 'Recently Updated',
                      icon: 'time',
                    },
                  ].map((option) => (
                    <TouchableOpacity
                      key={option.value}
                      style={[
                        styles.sortOption,
                        {
                          backgroundColor: theme.colors.background,
                          borderColor: theme.colors.border,
                        },
                        sortBy === option.value && {
                          backgroundColor: theme.colors.primary + '20',
                          borderColor: theme.colors.primary,
                        },
                      ]}
                      onPress={() => setSortBy(option.value as SortOption)}
                    >
                      <Ionicons
                        name={option.icon as any}
                        size={20}
                        color={
                          sortBy === option.value
                            ? theme.colors.primary
                            : theme.colors.textSecondary
                        }
                      />
                      <Text
                        style={[
                          styles.sortOptionText,
                          { color: theme.colors.text },
                          sortBy === option.value && {
                            color: theme.colors.primary,
                            fontWeight: '600',
                          },
                        ]}
                      >
                        {option.label}
                      </Text>
                      {sortBy === option.value && (
                        <Ionicons name="checkmark-circle" size={20} color={theme.colors.primary} />
                      )}
                    </TouchableOpacity>
                  ))}
                </View>
              </View>
            </ScrollView>

            {/* Modal Footer */}
            <View style={[styles.modalFooter, { borderTopColor: theme.colors.border }]}>
              <TouchableOpacity
                style={[
                  styles.modalButton,
                  styles.modalButtonSecondary,
                  { borderColor: theme.colors.border },
                ]}
                onPress={clearAllFilters}
              >
                <Text style={[styles.modalButtonText, { color: theme.colors.text }]}>
                  {i18n.language === 'ar' ? 'إعادة تعيين' : 'Reset'}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[
                  styles.modalButton,
                  styles.modalButtonPrimary,
                  { backgroundColor: theme.colors.primary },
                ]}
                onPress={() => setShowFiltersModal(false)}
              >
                <Text style={[styles.modalButtonText, { color: '#ffffff' }]}>
                  {i18n.language === 'ar' ? 'تطبيق' : 'Apply'}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Bulk Zone Change Modal */}
      <Modal
        visible={showBulkZoneModal}
        animationType="slide"
        transparent={true}
        onRequestClose={() => setShowBulkZoneModal(false)}
      >
        <View style={styles.modalOverlay}>
          <TouchableOpacity
            style={StyleSheet.absoluteFill}
            activeOpacity={1}
            onPress={() => setShowBulkZoneModal(false)}
          />
          <View
            style={[styles.modalContent, { backgroundColor: theme.colors.card, maxHeight: '70%' }]}
          >
            <View style={[styles.modalHeader, { borderBottomColor: theme.colors.border }]}>
              <Text style={[styles.modalTitle, { color: theme.colors.text }]}>
                {i18n.language === 'ar' ? 'اختر المنطقة' : 'Select Zone'}
              </Text>
              <TouchableOpacity onPress={() => setShowBulkZoneModal(false)}>
                <Ionicons name="close" size={28} color={theme.colors.text} />
              </TouchableOpacity>
            </View>

            {zones.length === 0 ? (
              <View style={{ padding: 40, alignItems: 'center' }}>
                <Ionicons name="location-outline" size={64} color={theme.colors.textSecondary} />
                <Text
                  style={[
                    styles.emptyStateText,
                    { color: theme.colors.textSecondary, marginTop: 16, marginBottom: 24 },
                  ]}
                >
                  {i18n.language === 'ar' ? 'لا توجد مناطق متاحة' : 'No zones available'}
                </Text>
                <Text
                  style={[
                    styles.emptyStateText,
                    { color: theme.colors.textSecondary, fontSize: 14, marginBottom: 16 },
                  ]}
                >
                  {i18n.language === 'ar'
                    ? 'يرجى إنشاء منطقة من خلال لوحة التحكم على الويب أولاً'
                    : 'Please create a zone from the web dashboard first'}
                </Text>
                <TouchableOpacity
                  style={[styles.refreshButton, { backgroundColor: theme.colors.primary }]}
                  onPress={() => refetch()}
                >
                  <Ionicons name="refresh" size={20} color="#ffffff" />
                  <Text style={styles.refreshButtonText}>
                    {i18n.language === 'ar' ? 'تحديث' : 'Refresh'}
                  </Text>
                </TouchableOpacity>
              </View>
            ) : (
              <ScrollView
                style={styles.zoneList}
                contentContainerStyle={{ paddingBottom: 20 }}
                nestedScrollEnabled={true}
              >
                {zones.map((zone) => (
                  <TouchableOpacity
                    key={zone.id}
                    style={[styles.zoneItem, { borderBottomColor: theme.colors.border }]}
                    onPress={() => handleBulkZoneChange(zone.id)}
                    activeOpacity={0.7}
                  >
                    <Ionicons name="location" size={24} color={theme.colors.primary} />
                    <Text style={[styles.zoneItemText, { color: theme.colors.text }]}>
                      {zone.name}
                    </Text>
                    <Ionicons name="chevron-forward" size={20} color={theme.colors.textSecondary} />
                  </TouchableOpacity>
                ))}
              </ScrollView>
            )}
          </View>
        </View>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  centered: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    fontSize: 16,
  },
  // New Header Design
  headerContainer: {
    paddingHorizontal: 20,
    paddingTop: 16,
    paddingBottom: 12,
  },
  pageTitleSection: {
    marginBottom: 12,
  },
  pageTitle: {
    fontSize: 26,
    fontWeight: '700',
    letterSpacing: -0.5,
    marginBottom: 4,
  },
  pageSubtitle: {
    fontSize: 14,
    fontWeight: '500',
  },
  selectionHintText: {
    fontSize: 12,
    marginTop: 4,
    opacity: 0.9,
  },
  // Compact KPI Chips
  statsBanner: {
    paddingHorizontal: 16,
    paddingBottom: 12,
    borderBottomWidth: 1,
  },
  selectionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  kpiRow: {
    flexDirection: 'row',
    gap: 8,
    flexWrap: 'wrap',
  },
  kpiChip: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
    borderWidth: 1,
    gap: 5,
  },
  kpiValue: {
    fontSize: 15,
    fontWeight: '700',
  },
  kpiLabel: {
    fontSize: 12,
    fontWeight: '600',
  },
  selectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderBottomWidth: 1,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 4,
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)',
  },
  selectionHeaderButton: {
    padding: 4,
  },
  selectionHeaderText: {
    flex: 1,
    fontSize: 16,
    fontWeight: '700',
    textAlign: 'center',
  },
  selectionHeaderButtonText: {
    fontSize: 14,
    fontWeight: '600',
  },
  searchContainer: {
    paddingHorizontal: 20,
    paddingTop: 12,
    paddingBottom: 12,
  },
  searchBar: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 12,
    borderWidth: 1,
    gap: 12,
  },
  searchInput: {
    flex: 1,
    fontSize: 15,
    padding: 0,
  },
  clearSearchButton: {
    padding: 2,
  },
  inlineFilterButton: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    position: 'relative',
  },
  filtersBadgeSmall: {
    position: 'absolute',
    top: 0,
    right: 0,
    backgroundColor: '#ef4444',
    width: 14,
    height: 14,
    borderRadius: 7,
    justifyContent: 'center',
    alignItems: 'center',
  },
  filtersBadgeText: {
    color: '#ffffff',
    fontSize: 11,
    fontWeight: 'bold',
  },
  activeFiltersContainer: {
    paddingVertical: 8,
    paddingHorizontal: 8,
  },
  activeFiltersScroll: {
    paddingHorizontal: 8,
    gap: 8,
    alignItems: 'center',
  },
  filterChip: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    borderWidth: 1,
    gap: 6,
  },
  filterChipText: {
    fontSize: 13,
    fontWeight: '500',
  },
  clearFiltersText: {
    fontSize: 13,
    fontWeight: '600',
    paddingHorizontal: 12,
  },
  filterTabsContainer: {
    flexDirection: 'row',
    paddingHorizontal: 12,
    paddingVertical: 8,
    gap: 6,
    borderTopWidth: 1,
  },
  quickFiltersContainer: {
    flexDirection: 'row',
    paddingHorizontal: 20,
    paddingBottom: 8,
  },
  quickFilterChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    borderWidth: 1,
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  quickFilterText: {
    fontSize: 12,
    fontWeight: '600',
  },
  filterTab: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 10,
    borderRadius: 8,
    gap: 4,
  },
  filterTabText: {
    fontSize: 14,
    fontWeight: '500',
  },
  filterTabCount: {
    fontSize: 12,
    fontWeight: '500',
  },
  listContent: {
    padding: 20,
    paddingBottom: 24,
  },
  deviceCard: {
    padding: 10,
    borderRadius: 10,
    marginBottom: 8,
    borderWidth: 1,
    elevation: 1,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
    position: 'relative',
  },
  checkboxContainer: {
    position: 'absolute',
    top: 12,
    ...(I18nManager.isRTL ? { left: 12 } : { right: 12 }),
    zIndex: 10,
  },
  checkbox: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 2,
    justifyContent: 'center',
    alignItems: 'center',
  },
  deviceHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
  },
  deviceIconContainer: {
    width: 38,
    height: 38,
    borderRadius: 19,
    justifyContent: 'center',
    alignItems: 'center',
    ...(I18nManager.isRTL ? { marginLeft: 10 } : { marginRight: 10 }),
  },
  deviceInfo: {
    flex: 1,
  },
  deviceName: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 1,
  },
  deviceType: {
    fontSize: 11,
    marginBottom: 2,
    textTransform: 'capitalize',
  },
  zoneContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginTop: 2,
  },
  deviceZone: {
    fontSize: 12,
  },
  readingsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    marginBottom: 6,
    paddingTop: 6,
    borderTopWidth: 1,
    borderTopColor: 'rgba(0,0,0,0.04)',
  },
  readingItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    paddingHorizontal: 6,
    paddingVertical: 3,
    borderRadius: 6,
    backgroundColor: 'rgba(0,0,0,0.02)',
  },
  tankReadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    width: '100%',
  },
  motionReadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    width: '100%',
  },
  doorReadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    width: '100%',
  },
  outletReadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    width: '100%',
  },
  outletStateBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 6,
    borderWidth: 1.5,
    gap: 4,
  },
  outletStateText: {
    fontSize: 12,
    fontWeight: '600',
  },
  airQualityReadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    flexWrap: 'wrap',
  },
  airQualityAqiBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 8,
    borderWidth: 2,
    gap: 6,
  },
  airQualityAqiText: {
    fontSize: 12,
    fontWeight: 'bold',
  },
  airQualityMeasurement: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  airQualityMeasurementText: {
    fontSize: 11,
  },
  // 💡 Light Sensor List Styles
  lightReadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    width: '100%',
  },
  lightLuxBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 8,
    borderWidth: 1.5,
    gap: 6,
  },
  lightLuxText: {
    fontSize: 12,
    fontWeight: 'bold',
  },
  lightBrightnessInfo: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  lightBrightnessText: {
    fontSize: 11,
  },
  // 🌡️ Pressure Sensor List Styles
  pressureReadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    width: '100%',
  },
  pressureBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 8,
    borderWidth: 1.5,
    gap: 6,
  },
  pressureText: {
    fontSize: 12,
    fontWeight: 'bold',
  },
  pressureWeatherBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    borderWidth: 1,
    gap: 4,
  },
  pressureWeatherText: {
    fontSize: 11,
  },
  // 🔥 Gas Sensor List Styles
  gasReadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    width: '100%',
  },
  gasConcentrationBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 8,
    borderWidth: 1.5,
    gap: 6,
  },
  gasConcentrationText: {
    fontSize: 12,
    fontWeight: 'bold',
  },
  gasToxicityBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
  },
  gasToxicityText: {
    fontSize: 11,
  },

  // Smoke Detector List Styles (v2.4.0)
  smokeReadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    width: '100%',
  },
  smokeDensityBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 8,
    borderWidth: 1.5,
  },
  smokeDensityText: {
    fontSize: 13,
    fontWeight: 'bold',
  },
  smokeLevelBadge: {
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 6,
    backgroundColor: 'rgba(156, 163, 175, 0.15)',
  },
  smokeLevelText: {
    fontSize: 11,
  },

  // 💧 Water Leak Sensor List Styles (v2.4.0)
  waterLeakReadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    width: '100%',
  },
  waterLevelBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 8,
  },
  waterLevelText: {
    fontSize: 13,
    fontWeight: 'bold',
    color: '#fff',
  },
  waterSeverityBadge: {
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 6,
  },
  waterSeverityText: {
    fontSize: 11,
    fontWeight: '600',
  },

  actuatorReadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    width: '100%',
  },
  actuatorStateBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 6,
    borderWidth: 1.5,
    gap: 4,
  },
  actuatorStateText: {
    fontSize: 12,
    fontWeight: '600',
  },
  energyReadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    width: '100%',
  },
  cameraReadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    width: '100%',
  },
  readingValue: {
    fontSize: 15,
    fontWeight: '600',
  },
  readingValueSecondary: {
    fontSize: 13,
    fontWeight: '500',
  },
  deviceFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 8,
  },
  deviceFooterLeft: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
  },
  statusBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 12,
    gap: 4,
  },
  statusText: {
    fontSize: 12,
    fontWeight: '600',
  },
  metricContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  metricText: {
    fontSize: 13,
  },
  lastSeen: {
    fontSize: 11,
  },
  emptyState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 80,
  },
  emptyIconContainer: {
    width: 120,
    height: 120,
    borderRadius: 60,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 20,
  },
  emptyText: {
    fontSize: 20,
    fontWeight: '600',
    marginBottom: 8,
  },
  emptySubtext: {
    fontSize: 14,
  },
  clearFiltersButton: {
    marginTop: 16,
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
  },
  clearFiltersButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
  },
  fab: {
    position: 'absolute',
    bottom: 90,
    width: 56,
    height: 56,
    borderRadius: 28,
    justifyContent: 'center',
    alignItems: 'center',
    elevation: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 4,
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)',
  },
  secondaryFab: {
    position: 'absolute',
    width: 48,
    height: 48,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
  },
  bulkActionsBar: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    flexDirection: 'row',
    padding: 16,
    gap: 12,
    borderTopWidth: 1,
    elevation: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: -4 },
    shadowOpacity: 0.2,
    shadowRadius: 8,
    boxShadow: '0px -4px 8px rgba(0, 0, 0, 0.2)',
  },
  bulkActionButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    borderRadius: 12,
    gap: 8,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
  },
  bulkActionButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
  },
  snackbar: {
    position: 'absolute',
    left: 16,
    right: 16,
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 12,
    elevation: 6,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.2,
    shadowRadius: 6,
    boxShadow: '0px 3px 6px rgba(0, 0, 0, 0.2)',
  },
  snackbarText: {
    color: '#ffffff',
    fontSize: 13,
    fontWeight: '600',
    textAlign: 'center',
  },
  zoneList: {
    maxHeight: 400,
  },
  zoneItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    gap: 12,
  },
  zoneItemText: {
    flex: 1,
    fontSize: 16,
    fontWeight: '500',
  },
  emptyStateText: {
    fontSize: 16,
    textAlign: 'center',
  },
  refreshButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
    gap: 8,
  },
  refreshButtonText: {
    color: '#ffffff',
    fontSize: 15,
    fontWeight: '600',
  },
  // Modal styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    maxHeight: '85%',
    elevation: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: -4 },
    shadowOpacity: 0.2,
    shadowRadius: 8,
    boxShadow: '0px -4px 8px rgba(0, 0, 0, 0.2)',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
  },
  modalBody: {
    maxHeight: 500,
  },
  filterSection: {
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(128, 128, 128, 0.1)',
  },
  filterSectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 12,
  },
  chipsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 20,
    borderWidth: 1.5,
    gap: 6,
  },
  chipText: {
    fontSize: 14,
    fontWeight: '500',
  },
  sortOptionsContainer: {
    gap: 8,
  },
  sortOption: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderRadius: 12,
    borderWidth: 1.5,
    gap: 12,
  },
  sortOptionText: {
    flex: 1,
    fontSize: 15,
  },
  modalFooter: {
    flexDirection: 'row',
    padding: 20,
    gap: 12,
    borderTopWidth: 1,
  },
  modalButton: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalButtonSecondary: {
    borderWidth: 1.5,
  },
  modalButtonPrimary: {
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
  },
  modalButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
});
